import { AnimatedSprite, Container, FederatedPointerEvent, Graphics, Rectangle, Sprite, Text, Texture } from "pixi.js";
import {
  BUILDING_DEFINITIONS,
  BUILDING_TYPES,
  GAME_CONFIG,
  UNIT_DEFINITIONS,
  buildingWorldX,
  laneEndXForSeat,
  oppositeSeat,
  projectWorldXForViewer,
} from "../shared/game-config.js";
import type {
  BuildingState,
  BuildingType,
  GameState,
  MatchEvent,
  Seat,
  UnitState,
  UnitType,
} from "../shared/game-types.js";
import {
  BOARD_LEFT,
  BOARD_TOP,
  BOARD_WORLD_WIDTH,
  COLORS,
  LANE_GAP,
  LANE_HEIGHT,
  TEXT_STYLE,
  VIEW_HEIGHT,
  VIEW_WIDTH,
} from "./theme.js";
import {
  BUILDING_ART_PATHS,
  GOLD_COIN_ART_PATH,
  LANE_GUARD_ART_PATH,
  UNIT_ANIMATION_PATHS,
  UNIT_ART_PATHS,
  buildingArtScaleX,
} from "./game-art.js";
import { createButton, createPanel, type ButtonView } from "./ui.js";
import {
  ParticleSystem,
  TimedEffectSystem,
  clamp01,
  easeOutBack,
  easeOutCubic,
  lerp,
} from "./animation.js";

type GameSceneActions = {
  placeBuilding: (lane: number, column: number, buildingType: BuildingType) => void;
  claimGold: (buildingId: string) => void;
  buyUnit: (buildingId: string, unitType: UnitType) => void;
  setDenQueueMode: (buildingId: string, enabled: boolean) => void;
  reorderUnitQueue: (buildingId: string, fromIndex: number, toIndex: number) => void;
  removeQueuedUnit: (buildingId: string, index: number) => void;
  releaseNextQueuedUnit: (buildingId: string) => void;
  releaseUnitQueue: (buildingId: string) => void;
  requestRestart: () => void;
};

type BuildingVisual = {
  container: Container;
  motion: Container;
  body: Graphics;
  art: Sprite;
  artScale: number;
  accent: Graphics;
  activity: Graphics;
  queue: Graphics;
  health: Graphics;
  badge: Container;
  badgeBackground: Graphics;
  badgeProgress: Graphics;
  badgeText: Text;
  type: BuildingType;
  owner: Seat;
  phase: number;
  spawnMs: number;
  hitMs: number;
  recoilMs: number;
  lastHealth: number;
  hovered: boolean;
  aimDirection: number;
  hasTarget: boolean;
};

type UnitVisual = {
  container: Container;
  motion: Container;
  body: Graphics;
  art: AnimatedSprite;
  artScale: number;
  shadow: Graphics;
  health: Graphics;
  type: UnitType;
  owner: Seat;
  phase: number;
  spawnMs: number;
  hitMs: number;
  attackMs: number;
  movingMs: number;
  lastHealth: number;
  lastServerX: number;
  hopMs: number;
  hopFromX: number;
  hopToX: number;
  targetX: number;
  targetY: number;
};

type BuildButton = {
  type: BuildingType;
  view: ButtonView;
  content: Container;
  cooldownIndicator: Graphics;
  cooldownText: Text;
  cooldownRemainingMs: number;
  cooldownDurationMs: number;
  cooldownRatio: number;
  shakeMs: number;
  phase: number;
};

type CostBadgeView = {
  container: Container;
  amount: Text;
};

type DenActionView = {
  view: ButtonView;
  content: Container;
  art: Sprite;
  cost: CostBadgeView;
  marker: Text;
};

const DEN_UNIT_TYPES = ["wolf", "rabbit", "beaver", "hedgehog"] as const satisfies readonly UnitType[];

const UNIT_SHORT_NAMES: Record<UnitType, string> = {
  wolf: "Varg",
  rabbit: "Hare",
  beaver: "Bäver",
  hedgehog: "Igelkott",
};

const UNIT_QUEUE_COLORS: Record<UnitType, number> = {
  wolf: 0x789873,
  rabbit: 0xd9c69d,
  beaver: 0x9b653c,
  hedgehog: 0xcaa870,
};

const DEN_QUEUE_SLOT_START_X = 114;
const DEN_QUEUE_SLOT_STEP = 36;
const DEN_QUEUE_SLOT_TOP = 134;
const DEN_QUEUE_SLOT_WIDTH = 34;
const DEN_QUEUE_SLOT_HEIGHT = 40;

type BuildingDragState = {
  type: BuildingType;
  pointerId: number;
  startX: number;
  startY: number;
  moved: boolean;
  preview: Sprite;
};

type DenQueueDragState = {
  pointerId: number;
  fromIndex: number;
  targetIndex: number;
  startX: number;
  startY: number;
  moved: boolean;
};

type AmbientMote = {
  display: Graphics;
  baseX: number;
  baseY: number;
  phase: number;
  speed: number;
  drift: number;
  rise: number;
  kind: "pollen" | "firefly";
};

type AmbientSway = {
  display: Container;
  baseRotation: number;
  phase: number;
  speed: number;
  amplitude: number;
};

type GuardVisual = {
  container: Container;
  ram: Sprite;
  body: Graphics;
  owner: Seat;
  lane: number;
  ready: boolean;
  phase: number;
  ramScale: number;
};

function laneCenter(lane: number): number {
  return BOARD_TOP + lane * (LANE_HEIGHT + LANE_GAP) + LANE_HEIGHT / 2;
}

function calculateUnitFormationOffsets(
  state: GameState,
  viewer: Seat,
): Map<string, { x: number; y: number }> {
  const offsets = new Map<string, { x: number; y: number }>();
  const groups = new Map<string, UnitState[]>();
  state.units.forEach(function (unit) {
    if (unit.health <= 0) {
      return;
    }
    const key = unit.owner + ":" + unit.lane;
    const group = groups.get(key) ?? [];
    group.push(unit);
    groups.set(key, group);
  });
  groups.forEach(function (units) {
    const screenDirection = units[0]?.owner === viewer ? 1 : -1;
    units.sort(function (left, right) {
      const leftX = projectWorldXForViewer(left.x, viewer);
      const rightX = projectWorldXForViewer(right.x, viewer);
      return screenDirection * (rightX - leftX);
    });
    let clusterIndex = 0;
    let previousX: number | null = null;
    units.forEach(function (unit) {
      const screenX = projectWorldXForViewer(unit.x, viewer);
      if (previousX === null || Math.abs(screenX - previousX) > GAME_CONFIG.unitFormationProximity) {
        clusterIndex = 0;
      } else {
        clusterIndex += 1;
      }
      const row = Math.floor(clusterIndex / 3);
      const slot = clusterIndex % 3;
      const lateralY = slot === 1 ? -18 : slot === 2 ? 18 : 0;
      const trailingX = row * 26 + (slot === 0 ? 0 : 14);
      offsets.set(unit.id, {
        x: -screenDirection * trailingX,
        y: lateralY,
      });
      previousX = screenX;
    });
  });
  return offsets;
}

function drawHealthBar(graphics: Graphics, health: number, maxHealth: number, width: number): void {
  graphics.clear();
  if (health >= maxHealth) {
    return;
  }
  const ratio = Math.max(0, Math.min(1, health / maxHealth));
  const color = ratio > 0.55 ? 0x77cf79 : ratio > 0.25 ? COLORS.gold : COLORS.danger;
  graphics.roundRect(-width / 2, -3, width, 6, 3)
    .fill({ color: COLORS.ink, alpha: 0.85 })
    .roundRect(-width / 2 + 1, -2, Math.max(0, (width - 2) * ratio), 4, 2)
    .fill(color);
}

function drawPlacementMarker(graphics: Graphics, lane: number, column: number): void {
  const seed = lane * GAME_CONFIG.columnsPerSide + column;
  const rotationOffset = (seed % 5) * 0.055;
  graphics.roundRect(-32, -39, 64, 78, 12)
    .fill({ color: COLORS.ownDark, alpha: 0.001 })
    .ellipse(0, 11, 30, 23)
    .fill({ color: 0x667b35, alpha: 0.12 })
    .stroke({ color: 0x52642d, width: 2.2, alpha: 0.46 });

  for (let index = 0; index < 9; index += 1) {
    const angle = rotationOffset + (Math.PI * 2 * index) / 9;
    const x = Math.cos(angle) * (28 + ((seed + index) % 3));
    const y = 11 + Math.sin(angle) * (21 + ((seed + index * 2) % 2));
    const radius = 1.4 + ((seed * 3 + index) % 3) * 0.35;
    graphics.circle(x, y, radius).fill({
      color: index % 3 === 0 ? 0xb9aa73 : 0x788348,
      alpha: index % 3 === 0 ? 0.62 : 0.5,
    });
  }

  const leafSide = seed % 2 === 0 ? -1 : 1;
  graphics.ellipse(leafSide * 24, 12, 4.5, 1.7)
    .fill({ color: 0x405a2b, alpha: 0.62 });
}

function createArtSprite(path: string, maxWidth: number, maxHeight: number): { sprite: Sprite; scale: number } {
  const sprite = Sprite.from(path);
  sprite.anchor.set(0.5);
  if (sprite.texture === Texture.EMPTY || sprite.texture.width <= 1 || sprite.texture.height <= 1) {
    sprite.visible = false;
    return { sprite, scale: 1 };
  }
  const scale = Math.min(maxWidth / sprite.texture.width, maxHeight / sprite.texture.height);
  sprite.scale.set(scale);
  return { sprite, scale };
}

function createCostBadge(cost: number, width = 64): CostBadgeView {
  const container = new Container();
  const background = new Graphics().roundRect(0, 0, width, 24, 12)
    .fill({ color: COLORS.ink, alpha: 0.9 })
    .stroke({ color: COLORS.goldDark, width: 1.5, alpha: 0.95 });
  const { sprite: coin } = createArtSprite(GOLD_COIN_ART_PATH, 18, 18);
  coin.position.set(14, 12);
  const amount = new Text({
    text: String(cost),
    style: { ...TEXT_STYLE, fontSize: 14, fill: COLORS.gold, fontWeight: "700" },
  });
  amount.anchor.set(0.5);
  amount.position.set(width / 2 + 8, 12);
  container.addChild(background, coin, amount);
  return { container, amount };
}

function drawBuildingBody(graphics: Graphics, building: BuildingState, isOwn: boolean): void {
  const accent = isOwn ? COLORS.own : COLORS.enemy;
  const dark = isOwn ? COLORS.ownDark : COLORS.enemyDark;
  graphics.clear();

  if (building.type === "gold_mine") {
    graphics
      .ellipse(0, 13, 35, 23).fill({ color: 0x5a554b }).stroke({ color: COLORS.ink, width: 2 })
      .circle(-21, 0, 16).fill({ color: 0x777066 })
      .circle(22, 1, 17).fill({ color: 0x6a645c })
      .ellipse(0, 13, 22, 16).fill({ color: 0x191713 })
      .rect(-31, -29, 7, 45).fill({ color: 0x68482d })
      .rect(24, -29, 7, 45).fill({ color: 0x68482d })
      .rect(-33, -31, 66, 8).fill({ color: dark })
      .circle(-10, 8, 6).fill({ color: COLORS.gold })
      .circle(7, 14, 5).fill({ color: COLORS.gold });
  } else if (building.type === "watchtower") {
    graphics
      .rect(-23, -19, 8, 43).fill({ color: 0x68482d })
      .rect(15, -19, 8, 43).fill({ color: 0x68482d })
      .roundRect(-31, -27, 62, 18, 4).fill({ color: dark }).stroke({ color: COLORS.ink, width: 2 })
      .poly([-36, -27, 0, -45, 36, -27]).fill({ color: accent }).stroke({ color: COLORS.ink, width: 2 })
      .rect(-31, 19, 62, 8).fill({ color: 0x4c3423 });
  } else if (building.type === "wolf_den") {
    graphics
      .ellipse(0, 14, 37, 23).fill({ color: 0x5a554b }).stroke({ color: COLORS.ink, width: 2 })
      .circle(-22, 0, 16).fill({ color: 0x777066 })
      .circle(22, 0, 17).fill({ color: 0x6a645c })
      .ellipse(0, 12, 23, 17).fill({ color: 0x191713 })
      .poly([-29, 8, -15, -30, -1, 7]).fill({ color: dark })
      .poly([1, 7, 15, -30, 29, 8]).fill({ color: dark })
      .circle(-8, -8, 3).fill({ color: accent })
      .circle(8, -8, 3).fill({ color: accent });
  } else if (building.type === "mushroom") {
    graphics
      .ellipse(0, 23, 29, 8).fill({ color: 0x3f542b, alpha: 0.8 })
      .roundRect(-8, -2, 16, 29, 7).fill({ color: 0xe7d9b9 }).stroke({ color: COLORS.ink, width: 2 })
      .ellipse(0, -8, 34, 20).fill({ color: 0xc94635 }).stroke({ color: COLORS.ink, width: 2 })
      .circle(-16, -12, 3.2).fill({ color: COLORS.parchment })
      .circle(2, -18, 3.8).fill({ color: COLORS.parchment })
      .circle(16, -8, 2.8).fill({ color: COLORS.parchment })
      .circle(-3, -5, 2.4).fill({ color: COLORS.parchment });
  } else {
    graphics
      .roundRect(-36, -31, 72, 60, 7).fill({ color: 0x625f58 }).stroke({ color: COLORS.ink, width: 3 })
      .roundRect(-31, -25, 28, 20, 4).fill({ color: 0x89847a }).stroke({ color: dark, width: 2 })
      .roundRect(4, -25, 27, 20, 4).fill({ color: 0x77736b }).stroke({ color: dark, width: 2 })
      .roundRect(-31, 0, 20, 22, 4).fill({ color: 0x77736b }).stroke({ color: dark, width: 2 })
      .roundRect(-6, 0, 37, 22, 4).fill({ color: 0x928c81 }).stroke({ color: dark, width: 2 })
      .poly([-36, -31, -18, -43, 0, -31, 18, -43, 36, -31]).stroke({ color: accent, width: 5 });
  }
}

function drawUnit(graphics: Graphics, unitType: UnitType, isOwn: boolean): void {
  const fur = isOwn ? 0x8ca286 : 0xb1847b;
  const dark = isOwn ? COLORS.ownDark : COLORS.enemyDark;
  if (unitType === "rabbit") {
    graphics.clear()
      .ellipse(-1, 5, 22, 14).fill({ color: fur }).stroke({ color: COLORS.ink, width: 2 })
      .circle(18, -3, 14).fill({ color: fur }).stroke({ color: COLORS.ink, width: 2 })
      .ellipse(13, -24, 6, 18).fill({ color: dark }).stroke({ color: COLORS.ink, width: 2 })
      .ellipse(24, -23, 6, 19).fill({ color: fur }).stroke({ color: COLORS.ink, width: 2 })
      .circle(24, -5, 2.5).fill({ color: COLORS.gold })
      .circle(-23, 1, 7).fill({ color: COLORS.parchment })
      .rect(-13, 15, 8, 10).fill({ color: dark })
      .rect(9, 15, 8, 10).fill({ color: dark });
    return;
  }
  if (unitType === "beaver") {
    graphics.clear()
      .ellipse(-2, 4, 24, 17).fill({ color: fur }).stroke({ color: COLORS.ink, width: 2 })
      .circle(20, -2, 14).fill({ color: fur }).stroke({ color: COLORS.ink, width: 2 })
      .ellipse(-30, 4, 15, 24).fill({ color: 0x6b4936 }).stroke({ color: COLORS.ink, width: 2 })
      .rect(27, 1, 8, 11).fill({ color: COLORS.parchment })
      .rect(-13, 15, 7, 10).fill({ color: dark })
      .rect(10, 15, 7, 10).fill({ color: dark });
    return;
  }
  if (unitType === "hedgehog") {
    graphics.clear()
      .poly([-34, 5, -28, -17, -18, -8, -8, -25, 1, -10, 12, -25, 18, -7, 29, -14, 27, 12])
      .fill({ color: 0x70513c })
      .stroke({ color: COLORS.ink, width: 2 })
      .ellipse(11, 5, 22, 14).fill({ color: fur }).stroke({ color: COLORS.ink, width: 2 })
      .circle(29, 0, 3).fill({ color: COLORS.ink })
      .rect(-7, 15, 7, 8).fill({ color: dark })
      .rect(14, 15, 7, 8).fill({ color: dark });
    return;
  }
  graphics.clear()
    .ellipse(-2, 3, 24, 15).fill({ color: fur }).stroke({ color: COLORS.ink, width: 2 })
    .poly([12, -2, 29, -13, 25, 7]).fill({ color: fur }).stroke({ color: COLORS.ink, width: 2 })
    .poly([16, -9, 18, -23, 24, -12]).fill({ color: dark })
    .poly([25, -11, 31, -22, 31, -7]).fill({ color: dark })
    .circle(25, -7, 2.5).fill({ color: COLORS.gold })
    .poly([-24, 0, -39, -11, -30, 8]).fill({ color: fur }).stroke({ color: COLORS.ink, width: 2 })
    .rect(-14, 12, 6, 14).fill({ color: dark })
    .rect(8, 12, 6, 14).fill({ color: dark });
}

export class GameScene {
  readonly container = new Container();
  private readonly seat: Seat;
  private readonly roomCode: string;
  private readonly actions: GameSceneActions;
  private readonly ambientLayer = new Container();
  private readonly guardLayer = new Container();
  private readonly buildingLayer = new Container();
  private readonly unitLayer = new Container();
  private readonly effectsLayer = new Container();
  private readonly particleLayer = new Container();
  private readonly dragLayer = new Container();
  private readonly denPopup: Container;
  private readonly denActions = new Map<UnitType, DenActionView>();
  private readonly denModeButton: ButtonView;
  private readonly denRemoveButton: ButtonView;
  private readonly denReleaseNextButton: ButtonView;
  private readonly denReleaseAllButton: ButtonView;
  private readonly denQueueText: Text;
  private readonly denQueueSlots: Container[] = [];
  private readonly denQueueSlotBackgrounds: Graphics[] = [];
  private readonly denQueueIcons: Sprite[] = [];
  private readonly overlayLayer = new Container();
  private readonly ownNameText: Text;
  private readonly enemyNameText: Text;
  private readonly ownResourceText: Text;
  private readonly enemyResourceText: Text;
  private readonly timerText: Text;
  private readonly matchText: Text;
  private readonly connectionText: Text;
  private readonly restartButton: ButtonView;
  private readonly buildButtons: BuildButton[] = [];
  private readonly guardVisuals = new Map<string, GuardVisual>();
  private readonly buildingVisuals = new Map<string, BuildingVisual>();
  private readonly unitVisuals = new Map<string, UnitVisual>();
  private readonly renderedEventIds = new Set<number>();
  private readonly ambientMotes: AmbientMote[] = [];
  private readonly ambientSways: AmbientSway[] = [];
  private readonly particles: ParticleSystem;
  private readonly timedEffects: TimedEffectSystem;
  private readonly lightRays = new Graphics();
  private readonly forestEyes = new Graphics();
  private readonly birdFlock = new Graphics();
  private state: GameState | null = null;
  private selectedBuildingType: BuildingType | null = null;
  private selectedDenId: string | null = null;
  private selectedDenQueueIndex: number | null = null;
  private denQueueDrag: DenQueueDragState | null = null;
  private buildingDrag: BuildingDragState | null = null;
  private overlayMode = "";
  private toastText: Text | null = null;
  private toastRemainingMs = 0;
  private elapsedMs = 0;
  private ownGoldPulseMs = 0;
  private enemyGoldPulseMs = 0;
  private displayedOwnGold: number | null = null;
  private displayedEnemyGold: number | null = null;
  private shakeMs = 0;
  private shakeStrength = 0;
  private suppressRemovalEffects = false;

  constructor(roomCode: string, seat: Seat, actions: GameSceneActions) {
    this.roomCode = roomCode;
    this.seat = seat;
    this.actions = actions;
    this.particles = new ParticleSystem(this.particleLayer);
    this.timedEffects = new TimedEffectSystem(this.effectsLayer);
    this.container.eventMode = "static";
    this.container.hitArea = new Rectangle(0, 0, VIEW_WIDTH, VIEW_HEIGHT);
    this.container.on("pointertap", (event) => this.handleScenePress(event));
    this.container.addChild(this.ambientLayer);
    this.drawAmbientForest();
    this.drawBoard();
    this.container.addChild(
      this.guardLayer,
      this.buildingLayer,
      this.unitLayer,
      this.effectsLayer,
      this.particleLayer,
      this.dragLayer,
    );

    const ownPanel = createPanel(370, 84, 0.88);
    ownPanel.position.set(28, 22);
    this.container.addChild(ownPanel);
    this.ownNameText = this.createHudText("Du", 52, 40, 22, COLORS.own);
    const { sprite: ownCoin } = createArtSprite(GOLD_COIN_ART_PATH, 25, 25);
    ownCoin.position.set(58, 73);
    this.container.addChild(ownCoin);
    this.ownResourceText = this.createHudText("0", 77, 73, 21, COLORS.gold);

    const enemyPanel = createPanel(370, 84, 0.88);
    enemyPanel.position.set(VIEW_WIDTH - 398, 22);
    this.container.addChild(enemyPanel);
    this.enemyNameText = this.createHudText("Motståndare", VIEW_WIDTH - 52, 40, 22, COLORS.enemy, 1);
    const { sprite: enemyCoin } = createArtSprite(GOLD_COIN_ART_PATH, 25, 25);
    enemyCoin.position.set(VIEW_WIDTH - 127, 73);
    this.container.addChild(enemyCoin);
    this.enemyResourceText = this.createHudText("0", VIEW_WIDTH - 52, 73, 21, COLORS.gold, 1);

    this.timerText = this.createHudText("00:00", VIEW_WIDTH / 2, 45, 28, COLORS.gold, 0.5);
    this.matchText = this.createHudText("MATCH " + roomCode + " • OMGÅNG 1", VIEW_WIDTH / 2, 76, 13, COLORS.parchmentDark, 0.5);
    this.matchText.style.letterSpacing = 2;

    this.restartButton = createButton({
      width: 160,
      height: 32,
      label: "Starta om",
      fontSize: 13,
      fill: COLORS.panelLight,
      onPress: () => this.actions.requestRestart(),
    });
    this.restartButton.container.position.set(620, 91);
    this.container.addChild(this.restartButton.container);

    this.connectionText = this.createHudText("ANSLUTEN", VIEW_WIDTH - 34, VIEW_HEIGHT - 18, 12, COLORS.own, 1);
    this.connectionText.style.letterSpacing = 2;

    this.drawToolbar();
    this.denPopup = new Container();
    this.denPopup.addChild(createPanel(406, 228, 0.98));
    DEN_UNIT_TYPES.forEach((unitType, index) => {
      const action = this.createDenAction(
        UNIT_ART_PATHS[unitType],
        UNIT_DEFINITIONS[unitType].cost,
        UNIT_SHORT_NAMES[unitType],
        () => {
          if (this.selectedDenId) {
            this.actions.buyUnit(this.selectedDenId, unitType);
          }
        },
      );
      action.view.container.position.set(8 + index * 98, 8);
      this.denActions.set(unitType, action);
      this.denPopup.addChild(action.view.container);
    });
    this.denModeButton = createButton({
      width: 126,
      height: 42,
      label: "LÄGE: DIREKT",
      fontSize: 12,
      fill: COLORS.panelLight,
      onPress: () => {
        const den = this.selectedDenId
          ? this.state?.buildings.find((building) => building.id === this.selectedDenId)
          : null;
        if (den) {
          this.actions.setDenQueueMode(den.id, !den.queueMode);
        }
      },
    });
    this.denModeButton.container.position.set(8, 104);
    this.denRemoveButton = createButton({
      width: 126,
      height: 42,
      label: "TA BORT",
      fontSize: 12,
      fill: COLORS.panelLight,
      onPress: () => {
        if (this.selectedDenId && this.selectedDenQueueIndex !== null) {
          this.actions.removeQueuedUnit(this.selectedDenId, this.selectedDenQueueIndex);
          this.selectedDenQueueIndex = null;
        }
      },
    });
    this.denRemoveButton.container.position.set(272, 104);
    this.denReleaseNextButton = createButton({
      width: 190,
      height: 38,
      label: "SLÄPP NÄSTA",
      fontSize: 12,
      fill: COLORS.panelLight,
      onPress: () => {
        if (this.selectedDenId) {
          this.actions.releaseNextQueuedUnit(this.selectedDenId);
          this.selectedDenQueueIndex = null;
        }
      },
    });
    this.denReleaseNextButton.container.position.set(8, 181);
    this.denReleaseAllButton = createButton({
      width: 190,
      height: 38,
      label: "SLÄPP ALLA",
      fontSize: 12,
      fill: COLORS.panelLight,
      onPress: () => {
        if (this.selectedDenId) {
          this.actions.releaseUnitQueue(this.selectedDenId);
          this.selectedDenQueueIndex = null;
        }
      },
    });
    this.denReleaseAllButton.container.position.set(208, 181);
    this.denQueueText = new Text({
      text: "KÖ 0/5 • DRA FÖR ATT ORDNA",
      style: { ...TEXT_STYLE, fontSize: 10, fill: COLORS.parchment, fontWeight: "700" },
    });
    this.denQueueText.anchor.set(0.5);
    this.denQueueText.position.set(203, 115);
    for (let index = 0; index < GAME_CONFIG.maxDenQueue; index += 1) {
      const slot = new Container();
      const background = new Graphics();
      const icon = new Sprite(Texture.EMPTY);
      icon.anchor.set(0.5);
      icon.position.set(DEN_QUEUE_SLOT_WIDTH / 2, 19);
      icon.visible = false;
      slot.addChild(background, icon);
      slot.position.set(DEN_QUEUE_SLOT_START_X + index * DEN_QUEUE_SLOT_STEP, DEN_QUEUE_SLOT_TOP);
      slot.hitArea = new Rectangle(0, 0, DEN_QUEUE_SLOT_WIDTH, DEN_QUEUE_SLOT_HEIGHT);
      slot.on("pointerdown", (event) => this.beginDenQueueDrag(index, event));
      slot.on("globalpointermove", (event) => this.moveDenQueueDrag(event));
      slot.on("pointerup", (event) => this.endDenQueueDrag(event));
      slot.on("pointerupoutside", (event) => this.endDenQueueDrag(event));
      this.denQueueSlots.push(slot);
      this.denQueueSlotBackgrounds.push(background);
      this.denQueueIcons.push(icon);
      this.denPopup.addChild(slot);
    }
    this.denPopup.addChild(
      this.denModeButton.container,
      this.denRemoveButton.container,
      this.denReleaseNextButton.container,
      this.denReleaseAllButton.container,
      this.denQueueText,
    );
    this.denPopup.visible = false;
    this.container.addChild(this.denPopup, this.overlayLayer);
  }

  update(state: GameState): void {
    const previousRound = this.state?.roundNumber;
    this.suppressRemovalEffects = previousRound !== undefined && previousRound !== state.roundNumber;
    if (previousRound !== undefined && previousRound !== state.roundNumber) {
      this.selectedBuildingType = null;
      this.selectedDenId = null;
      this.selectedDenQueueIndex = null;
      this.denQueueDrag = null;
      this.renderedEventIds.clear();
      this.particles.clear();
      this.timedEffects.clear();
      this.showToast("Omgång " + state.roundNumber + " har börjat!");
    }
    this.state = state;
    const own = state.players[this.seat];
    const enemy = state.players[oppositeSeat(this.seat)];
    if (this.displayedOwnGold !== null && this.displayedOwnGold !== own.gold) {
      this.ownGoldPulseMs = 360;
    }
    if (this.displayedEnemyGold !== null && this.displayedEnemyGold !== enemy.gold) {
      this.enemyGoldPulseMs = 360;
    }
    this.displayedOwnGold = own.gold;
    this.displayedEnemyGold = enemy.gold;
    this.ownNameText.text = own.name;
    this.enemyNameText.text = enemy.name;
    this.ownResourceText.text = String(own.gold);
    this.enemyResourceText.text = String(enemy.gold);
    const elapsedSeconds = Math.floor(state.nowMs / 1_000);
    this.timerText.text = String(Math.floor(elapsedSeconds / 60)).padStart(2, "0")
      + ":" + String(elapsedSeconds % 60).padStart(2, "0");
    this.matchText.text = (state.mode === "ai" ? "MOT AI" : "MATCH " + this.roomCode) + " • OMGÅNG " + state.roundNumber;

    this.updateLaneGuards(state);
    this.updateBuildings(state);
    this.updateUnits(state);
    this.updateEffects(state);
    this.updateToolbar(state);
    this.updateDenPopup(state);
    this.updateOverlay(state);
    this.suppressRemovalEffects = false;
  }

  tick(elapsedMs: number): void {
    const boundedElapsedMs = Math.min(100, Math.max(0, elapsedMs));
    this.elapsedMs += boundedElapsedMs;
    this.tickAmbient();
    this.tickGuardAnimations();
    this.tickBuildingAnimations(boundedElapsedMs);
    this.tickUnitAnimations(boundedElapsedMs);
    this.tickToolbarAnimations(boundedElapsedMs);
    this.particles.tick(boundedElapsedMs);
    this.timedEffects.tick(boundedElapsedMs);

    this.ownGoldPulseMs = Math.max(0, this.ownGoldPulseMs - boundedElapsedMs);
    this.enemyGoldPulseMs = Math.max(0, this.enemyGoldPulseMs - boundedElapsedMs);
    const ownGoldPulse = this.ownGoldPulseMs > 0
      ? Math.sin((1 - this.ownGoldPulseMs / 420) * Math.PI) * 0.16
      : 0;
    const enemyGoldPulse = this.enemyGoldPulseMs > 0
      ? Math.sin((1 - this.enemyGoldPulseMs / 420) * Math.PI) * 0.16
      : 0;
    this.ownResourceText.scale.set(1 + ownGoldPulse);
    this.enemyResourceText.scale.set(1 + enemyGoldPulse);

    if (this.shakeMs > 0) {
      this.shakeMs = Math.max(0, this.shakeMs - boundedElapsedMs);
      const strength = this.shakeStrength * Math.min(1, this.shakeMs / 220);
      this.container.position.set(
        Math.sin(this.elapsedMs * 0.073) * strength,
        Math.cos(this.elapsedMs * 0.091) * strength * 0.55,
      );
    } else {
      this.container.position.set(0, 0);
      this.shakeStrength = 0;
    }

    if (this.toastText) {
      this.toastRemainingMs -= boundedElapsedMs;
      this.toastText.alpha = Math.min(1, Math.max(0, this.toastRemainingMs / 300));
      const toastProgress = clamp01((2_300 - this.toastRemainingMs) / 280);
      this.toastText.scale.set(easeOutBack(toastProgress));
      if (this.toastRemainingMs <= 0) {
        this.toastText.destroy();
        this.toastText = null;
      }
    }
  }

  recoverTransientAnimations(): void {
    this.particles.clear();
    this.timedEffects.clear();
  }

  private tickBuildingAnimations(elapsedMs: number): void {
    this.buildingVisuals.forEach((visual) => {
      visual.spawnMs = Math.min(700, visual.spawnMs + elapsedMs);
      visual.hitMs = Math.max(0, visual.hitMs - elapsedMs);
      visual.recoilMs = Math.max(0, visual.recoilMs - elapsedMs);
      const spawnProgress = clamp01(visual.spawnMs / 520);
      const spawnScale = easeOutBack(spawnProgress);
      const hoverScale = visual.hovered ? 1.065 : 1;
      const time = this.elapsedMs + visual.phase * 1_000;
      let idleY = 0;
      let scaleX = 1;
      let scaleY = 1;
      let rotation = 0;
      if (visual.type === "gold_mine") {
        idleY = Math.sin(time * 0.0022) * 1.2;
        rotation = Math.sin(time * 0.0013) * 0.006;
      } else if (visual.type === "wolf_den") {
        const breath = Math.sin(time * 0.0025);
        idleY = breath * 0.7;
        scaleX = 1 + breath * 0.012;
        scaleY = 1 - breath * 0.008;
      } else if (visual.type === "watchtower") {
        idleY = Math.sin(time * 0.0017) * 0.65;
        rotation = Math.sin(time * 0.0012) * 0.009;
      } else if (visual.type === "mushroom") {
        const breathe = Math.sin(time * 0.0031);
        idleY = breathe * 1.1;
        rotation = Math.sin(time * 0.0017) * 0.018;
        scaleX = 1 + breathe * 0.018;
        scaleY = 1 - breathe * 0.012;
      } else if (visual.type === "sumpgap") {
        const breathe = Math.sin(time * 0.0034);
        idleY = breathe * 0.9;
        rotation = Math.sin(time * 0.0015) * 0.012;
        scaleX = 1 - breathe * 0.014;
        scaleY = 1 + breathe * 0.022;
      } else if (visual.type === "acorn_catapult") {
        idleY = Math.sin(time * 0.0018) * 0.65;
        rotation = Math.sin(time * 0.0011) * 0.008;
      }
      const hitProgress = visual.hitMs > 0 ? visual.hitMs / 300 : 0;
      const hitOffset = visual.hitMs > 0 ? Math.sin(visual.hitMs * 0.13) * 4 * hitProgress : 0;
      const recoilDuration = visual.type === "sumpgap"
        ? 900
        : visual.type === "acorn_catapult"
          ? 520
          : 280;
      const recoilProgress = visual.recoilMs > 0 ? visual.recoilMs / recoilDuration : 0;
      const recoilDirection = visual.aimDirection === 0 ? -1 : 1;
      const recoilStrength = visual.type === "acorn_catapult" ? 9 : 5;
      const recoilOffset = recoilProgress > 0
        ? recoilDirection * Math.sin(recoilProgress * Math.PI) * recoilStrength
        : 0;
      if (visual.type === "sumpgap" && recoilProgress > 0) {
        const chew = Math.sin(recoilProgress * Math.PI * 5) * recoilProgress;
        scaleX *= 1 + Math.abs(chew) * 0.11;
        scaleY *= 1 - Math.abs(chew) * 0.15;
      } else if (visual.type === "acorn_catapult" && recoilProgress > 0) {
        rotation += recoilDirection * Math.sin(recoilProgress * Math.PI) * 0.08;
      }
      visual.motion.position.set(
        hitOffset + recoilOffset,
        idleY + (1 - easeOutCubic(spawnProgress)) * 24,
      );
      visual.motion.rotation = rotation;
      visual.motion.scale.set(spawnScale * hoverScale * scaleX, spawnScale * hoverScale * scaleY);
      visual.art.tint = visual.hitMs > 0 ? 0xffb0a2 : 0xffffff;
      visual.badge.scale.set(
        visual.type === "gold_mine" && visual.badge.visible && visual.badgeText.text !== "0"
          ? 1 + Math.sin(time * 0.006) * 0.055
          : 1,
      );
      this.drawBuildingActivity(visual, time);
    });
  }

  private tickGuardAnimations(): void {
    this.guardVisuals.forEach((visual) => {
      if (!visual.ready) {
        visual.ram.position.set(0, 0);
        visual.ram.rotation = 0;
        return;
      }
      const time = this.elapsedMs * 0.0016 + visual.phase;
      const laneDirection = visual.owner === this.seat ? 1 : -1;
      const laneTravel = Math.sin(time * 0.72) * 7;
      visual.ram.position.set(laneDirection * laneTravel, 0);
      visual.ram.rotation = laneDirection * Math.sin(time * 0.72) * 0.025;
    });
  }

  private drawBuildingActivity(visual: BuildingVisual, time: number): void {
    const graphics = visual.activity;
    graphics.clear();
    if (visual.type === "gold_mine") {
      const pulse = 0.45 + (Math.sin(time * 0.007) + 1) * 0.18;
      graphics.circle(-7, 7, 9 + Math.sin(time * 0.004) * 1.5)
        .fill({ color: COLORS.gold, alpha: pulse * 0.18 });
      const wheelAngle = time * 0.0016;
      graphics.circle(-18, 10, 6).stroke({ color: 0xc18a3e, width: 1.4, alpha: 0.55 });
      for (let spoke = 0; spoke < 4; spoke += 1) {
        const angle = wheelAngle + spoke * Math.PI / 2;
        graphics.moveTo(-18, 10).lineTo(-18 + Math.cos(angle) * 5, 10 + Math.sin(angle) * 5)
          .stroke({ color: 0xd8aa58, width: 1, alpha: 0.52 });
      }
      const sparkle = (time * 0.0012) % 1;
      if (sparkle < 0.32) {
        const alpha = Math.sin(sparkle / 0.32 * Math.PI) * 0.9;
        graphics.moveTo(20, -8).lineTo(20, 0).moveTo(16, -4).lineTo(24, -4)
          .stroke({ color: COLORS.gold, width: 1.5, alpha });
      }
    } else if (visual.type === "wolf_den") {
      const eyeAlpha = 0.5 + (Math.sin(time * 0.005) + 1) * 0.2;
      graphics.circle(-5, 7, 1.8).fill({ color: COLORS.gold, alpha: eyeAlpha })
        .circle(5, 7, 1.8).fill({ color: COLORS.gold, alpha: eyeAlpha });
      for (let puff = 0; puff < 3; puff += 1) {
        const progress = (time * 0.00022 + puff / 3) % 1;
        graphics.circle(15 + Math.sin(progress * Math.PI * 2) * 3, -27 - progress * 17, 3 + progress * 4)
          .fill({ color: 0xb7b09b, alpha: (1 - progress) * 0.12 });
      }
    } else if (visual.type === "watchtower") {
      const searchingAngle = visual.hasTarget
        ? visual.aimDirection
        : visual.aimDirection + Math.sin(time * 0.0015) * 0.16;
      const directionX = Math.cos(searchingAngle);
      const directionY = Math.sin(searchingAngle);
      const originY = -23;
      graphics.circle(0, originY, 5).stroke({ color: 0x6d4829, width: 2, alpha: 0.82 })
        .moveTo(directionX * 2, originY + directionY * 2)
        .lineTo(directionX * 21, originY + directionY * 21)
        .stroke({ color: visual.hasTarget ? COLORS.gold : 0xc7a76a, width: 2.2, alpha: visual.hasTarget ? 0.9 : 0.45 });
      if (visual.hasTarget) {
        graphics.poly([
          directionX * 21, originY + directionY * 21,
          directionX * 15 - directionY * 3, originY + directionY * 15 + directionX * 3,
          directionX * 15 + directionY * 3, originY + directionY * 15 - directionX * 3,
        ]).fill({ color: COLORS.gold, alpha: 0.9 });
      }
    } else if (visual.type === "mushroom") {
      const warningPulse = 0.18 + (Math.sin(time * 0.004) + 1) * 0.07;
      graphics.ellipse(0, 20, 27 + Math.sin(time * 0.0026) * 2, 8)
        .fill({ color: 0x9dcc62, alpha: warningPulse });
      for (let spore = 0; spore < 5; spore += 1) {
        const progress = (time * 0.00018 + spore / 5) % 1;
        const drift = Math.sin(progress * Math.PI * 2 + spore) * 8;
        graphics.circle(-15 + spore * 7 + drift, 5 - progress * 45, 1.4 + progress * 1.2)
          .fill({
            color: spore % 2 === 0 ? 0xe8edbd : 0xa8cf6a,
            alpha: Math.sin(progress * Math.PI) * 0.48,
          });
      }
    } else if (visual.type === "sumpgap") {
      const drool = (time * 0.00038) % 1;
      graphics.circle(17 + Math.sin(time * 0.002) * 2, 8 + drool * 22, 2.2 + drool * 1.4)
        .fill({ color: 0xb7d57a, alpha: Math.sin(drool * Math.PI) * 0.62 });
      for (let bubble = 0; bubble < 3; bubble += 1) {
        const progress = (time * 0.00016 + bubble / 3) % 1;
        graphics.circle(-24 + bubble * 22, 25 - progress * 22, 2 + progress * 2)
          .stroke({ color: 0x9dbb67, width: 1.2, alpha: Math.sin(progress * Math.PI) * 0.44 });
      }
    } else if (visual.type === "acorn_catapult") {
      const tension = Math.sin(time * 0.0021);
      graphics.moveTo(-17, -25).quadraticCurveTo(0, -31 - tension * 2, 19, -22)
        .stroke({ color: 0xd2b98a, width: 1.5, alpha: 0.5 });
      const glint = (time * 0.0009) % 1;
      if (glint < 0.28) {
        const alpha = Math.sin(glint / 0.28 * Math.PI) * 0.85;
        graphics.moveTo(18, -30).lineTo(18, -21).moveTo(13.5, -25.5).lineTo(22.5, -25.5)
          .stroke({ color: COLORS.gold, width: 1.5, alpha });
      }
    } else if (visual.type === "wall") {
      for (let blade = 0; blade < 5; blade += 1) {
        const x = -28 + blade * 14;
        const lean = Math.sin(time * 0.0015 + blade) * 3;
        graphics.moveTo(x, 28).quadraticCurveTo(x + lean * 0.4, 21, x + lean, 17)
          .stroke({ color: blade % 2 === 0 ? 0x536f38 : 0x718548, width: 1.6, alpha: 0.58 });
      }
    }
  }

  private tickUnitAnimations(elapsedMs: number): void {
    const smoothing = 1 - Math.pow(0.002, elapsedMs / 1_000);
    this.unitVisuals.forEach((visual) => {
      visual.spawnMs = Math.min(620, visual.spawnMs + elapsedMs);
      visual.hitMs = Math.max(0, visual.hitMs - elapsedMs);
      visual.movingMs = Math.max(0, visual.movingMs - elapsedMs);
      if (visual.attackMs >= 0) {
        visual.attackMs += elapsedMs;
        if (visual.attackMs >= 430) {
          visual.attackMs = -1;
        }
      }
      let scriptedJumpY = 0;
      if (visual.hopMs >= 0) {
        visual.hopMs += elapsedMs;
        const hopProgress = clamp01(visual.hopMs / 520);
        const eased = easeOutCubic(hopProgress);
        visual.container.x = lerp(visual.hopFromX, visual.hopToX, eased);
        scriptedJumpY = -Math.sin(hopProgress * Math.PI) * 54;
        if (hopProgress >= 1) {
          visual.hopMs = -1;
          visual.container.x = visual.hopToX;
        }
      } else {
        visual.container.x += (visual.targetX - visual.container.x) * smoothing;
      }
      visual.container.y += (visual.targetY - visual.container.y) * smoothing;
      const moving = visual.movingMs > 0 && visual.attackMs < 0;
      if (visual.art.visible) {
        if (moving && !visual.art.playing) {
          visual.art.play();
        } else if (!moving && visual.art.playing) {
          visual.art.stop();
          visual.art.gotoAndStop(0);
        }
      }
      const direction = visual.owner === this.seat ? 1 : -1;
      const time = this.elapsedMs + visual.phase * 1_000;
      let locomotionY = 0;
      let locomotionRotation = 0;
      let squashX = 1;
      let squashY = 1;
      if (moving && visual.type === "rabbit" && visual.hopMs < 0) {
        const hopCycle = (time * 0.00165) % 1;
        locomotionY = -Math.sin(hopCycle * Math.PI) * 15;
        squashX = 1 + Math.cos(hopCycle * Math.PI * 2) * 0.045;
        squashY = 1 - Math.cos(hopCycle * Math.PI * 2) * 0.055;
      } else if (moving && visual.type === "beaver") {
        locomotionY = Math.sin(time * 0.017) * 3.2;
        locomotionRotation = direction * Math.sin(time * 0.017) * 0.035;
        squashX = 1 + Math.sin(time * 0.017) * 0.035;
        squashY = 1 - Math.sin(time * 0.017) * 0.025;
      } else if (moving && visual.type === "hedgehog") {
        locomotionY = -Math.abs(Math.sin(time * 0.021)) * 3.5;
        locomotionRotation = direction * Math.sin(time * 0.021) * 0.11;
        squashX = 1 + Math.sin(time * 0.021) * 0.045;
        squashY = 1 - Math.sin(time * 0.021) * 0.04;
      } else if (moving) {
        locomotionY = Math.sin(time * 0.018) * 2.6;
        squashX = 1 + Math.sin(time * 0.018) * 0.025;
        squashY = 1 - Math.sin(time * 0.018) * 0.018;
      } else {
        locomotionY = Math.sin(time * 0.003) * 0.8;
      }
      let attackOffsetX = 0;
      let attackRotation = 0;
      if (visual.attackMs >= 0) {
        const attackProgress = clamp01(visual.attackMs / 430);
        if (attackProgress < 0.25) {
          const anticipation = attackProgress / 0.25;
          attackOffsetX = -direction * anticipation * 6;
          squashX *= 0.9;
          squashY *= 1.08;
        } else if (attackProgress < 0.55) {
          const lunge = (attackProgress - 0.25) / 0.3;
          attackOffsetX = direction * Math.sin(lunge * Math.PI) * 17;
          attackRotation = direction * Math.sin(lunge * Math.PI) * 0.07;
          squashX *= 1.08;
          squashY *= 0.94;
        } else {
          const recover = (attackProgress - 0.55) / 0.45;
          attackOffsetX = direction * (1 - recover) * 4;
        }
        if (visual.type === "beaver") {
          attackRotation += direction * Math.sin(attackProgress * Math.PI) * 0.18;
          attackOffsetX *= 1.18;
        } else if (visual.type === "hedgehog") {
          attackRotation += direction * Math.sin(attackProgress * Math.PI) * 0.5;
          squashX *= 1.08;
          squashY *= 0.93;
        }
      }
      const hitProgress = visual.hitMs > 0 ? visual.hitMs / 260 : 0;
      const hitOffset = visual.hitMs > 0 ? -direction * Math.sin(visual.hitMs * 0.14) * 5 * hitProgress : 0;
      const spawnScale = easeOutBack(clamp01(visual.spawnMs / 430));
      const totalJumpY = scriptedJumpY + locomotionY;
      visual.motion.position.set(attackOffsetX + hitOffset, totalJumpY + (1 - easeOutCubic(clamp01(visual.spawnMs / 430))) * 16);
      visual.motion.rotation = locomotionRotation + attackRotation;
      visual.motion.scale.set(spawnScale * squashX, spawnScale * squashY);
      visual.art.tint = visual.hitMs > 0 ? 0xffaaa0 : 0xffffff;
      visual.shadow.scale.set(Math.max(0.58, 1 + totalJumpY / 115), Math.max(0.7, 1 + totalJumpY / 190));
      visual.shadow.alpha = 0.36 * Math.max(0.35, 1 + totalJumpY / 82);
    });
  }

  private tickToolbarAnimations(elapsedMs: number): void {
    const cooldownsAdvance = Boolean(this.state
      && this.state.phase === "playing"
      && this.state.players.p1.connected
      && this.state.players.p2.connected);
    this.buildButtons.forEach((entry) => {
      if (cooldownsAdvance) {
        entry.cooldownRemainingMs = Math.max(0, entry.cooldownRemainingMs - elapsedMs);
      }
      const targetRatio = entry.cooldownDurationMs > 0
        ? clamp01(entry.cooldownRemainingMs / entry.cooldownDurationMs)
        : 0;
      entry.cooldownRatio += (targetRatio - entry.cooldownRatio) * (1 - Math.pow(0.004, elapsedMs / 1_000));
      entry.shakeMs = Math.max(0, entry.shakeMs - elapsedMs);
      const selected = this.selectedBuildingType === entry.type;
      const idleY = selected ? -2 + Math.sin(this.elapsedMs * 0.007 + entry.phase) * 1.4 : 0;
      entry.content.position.set(
        entry.shakeMs > 0 ? Math.sin(entry.shakeMs * 0.12) * 5 * (entry.shakeMs / 300) : 0,
        idleY,
      );
      this.drawBuildButtonCooldown(entry);
    });
  }

  private drawBuildButtonCooldown(entry: BuildButton): void {
    entry.cooldownIndicator.clear();
    const active = entry.cooldownRemainingMs > 1 && entry.cooldownRatio > 0.001;
    entry.cooldownText.visible = active;
    if (!active) {
      return;
    }
    entry.cooldownIndicator.circle(91, 17, 15)
      .fill({ color: COLORS.ink, alpha: 0.92 })
      .stroke({ color: COLORS.parchmentDark, width: 3, alpha: 0.72 });
    entry.cooldownIndicator.moveTo(91, 2)
      .arc(91, 17, 15, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * entry.cooldownRatio)
      .stroke({ color: COLORS.gold, width: 3, alpha: 1 });
    entry.cooldownText.text = Math.ceil(entry.cooldownRemainingMs / 1_000) + "s";
  }

  private startShake(durationMs: number, strength: number): void {
    this.shakeMs = Math.max(this.shakeMs, durationMs);
    this.shakeStrength = Math.max(this.shakeStrength, strength);
  }

  setConnectionStatus(status: "connecting" | "connected" | "disconnected"): void {
    const statusLabels = {
      connecting: "ANSLUTER…",
      connected: "ANSLUTEN",
      disconnected: "ÅTERANSLUTER…",
    } as const;
    this.connectionText.text = statusLabels[status];
    this.connectionText.style.fill = status === "connected" ? COLORS.own : COLORS.danger;
  }

  showToast(message: string): void {
    this.toastText?.destroy();
    const toast = new Text({
      text: message,
      style: {
        ...TEXT_STYLE,
        fontSize: 18,
        fontWeight: "700",
        align: "center",
        fill: COLORS.parchment,
        stroke: { color: COLORS.ink, width: 5 },
      },
    });
    toast.anchor.set(0.5);
    toast.position.set(VIEW_WIDTH / 2, 635);
    this.container.addChild(toast);
    this.toastText = toast;
    this.toastRemainingMs = 2_300;
  }

  dispose(): void {
    this.particles.clear();
    this.timedEffects.clear();
    this.container.destroy({ children: true });
  }

  private createHudText(
    value: string,
    x: number,
    y: number,
    fontSize: number,
    fill: number,
    anchorX = 0,
  ): Text {
    const text = new Text({ text: value, style: { ...TEXT_STYLE, fontSize, fill, fontWeight: "700" } });
    text.anchor.set(anchorX, 0.5);
    text.position.set(x, y);
    this.container.addChild(text);
    return text;
  }

  private createDenAction(
    artPath: string,
    cost: number,
    name: string,
    onPress: () => void,
  ): DenActionView {
    const view = createButton({
      width: 90,
      height: 88,
      label: "",
      fill: COLORS.panelLight,
      onPress,
    });
    const content = new Container();
    const { sprite: art } = createArtSprite(artPath, 56, 38);
    art.position.set(45, 23);
    const nameText = new Text({
      text: name,
      style: { ...TEXT_STYLE, fontSize: 10, fill: COLORS.parchment, fontWeight: "700" },
    });
    nameText.anchor.set(0.5);
    nameText.position.set(45, 48);
    const costBadge = createCostBadge(cost, 60);
    costBadge.container.position.set(15, 58);
    const marker = new Text({
      text: "",
      style: {
        ...TEXT_STYLE,
        fontSize: 18,
        fill: COLORS.gold,
        fontWeight: "700",
        stroke: { color: COLORS.ink, width: 4 },
      },
    });
    marker.anchor.set(0.5);
    marker.position.set(72, 14);
    content.addChild(art, nameText, costBadge.container, marker);
    view.container.addChild(content);
    return { view, content, art, cost: costBadge, marker };
  }

  private drawAmbientForest(): void {
    this.lightRays
      .poly([360, 84, 515, 84, 680, 625, 580, 625])
      .fill({ color: 0xffefad, alpha: 0.035 })
      .poly([910, 88, 1_000, 88, 920, 620, 835, 620])
      .fill({ color: 0xdff6b0, alpha: 0.025 });
    this.ambientLayer.addChild(this.lightRays);

    const plantYs = [166, 247, 344, 449, 548, 620];
    plantYs.forEach((y, index) => {
      for (const side of [-1, 1] as const) {
        const plant = new Container();
        const leaves = new Graphics();
        const facing = side < 0 ? 1 : -1;
        leaves.moveTo(0, 18).quadraticCurveTo(facing * 7, -3, facing * 4, -28)
          .stroke({ color: 0x294b2c, width: 2.2, alpha: 0.46 });
        for (let leaf = 0; leaf < 5; leaf += 1) {
          const leafY = 10 - leaf * 9;
          const leafX = facing * (4 + (leaf % 2) * 5);
          leaves.ellipse(leafX, leafY, 8 - leaf * 0.55, 3.1)
            .fill({ color: leaf % 2 === 0 ? 0x416f38 : 0x668344, alpha: 0.3 + index * 0.012 });
        }
        plant.addChild(leaves);
        plant.position.set(side < 0 ? 18 + (index % 2) * 10 : VIEW_WIDTH - 18 - (index % 2) * 10, y);
        plant.pivot.set(0, 18);
        this.ambientLayer.addChild(plant);
        this.ambientSways.push({
          display: plant,
          baseRotation: (side < 0 ? 1 : -1) * 0.05,
          phase: index * 0.83 + (side > 0 ? 1.9 : 0),
          speed: 0.00042 + index * 0.000018,
          amplitude: 0.035 + (index % 3) * 0.008,
        });
      }
    });

    for (let index = 0; index < 30; index += 1) {
      const mote = new Graphics();
      const firefly = index < 7;
      if (firefly) {
        mote.circle(0, 0, 5).fill({ color: 0xd8ef85, alpha: 0.06 })
          .circle(0, 0, 1.4).fill({ color: 0xf5df71, alpha: 0.85 });
      } else {
        mote.circle(0, 0, 0.8 + (index % 3) * 0.35)
          .fill({ color: index % 2 === 0 ? 0xf2d582 : 0xe8efb5, alpha: 0.28 });
      }
      const edgeBias = index % 4 < 2;
      const baseX = edgeBias
        ? (index % 2 === 0 ? 25 + (index * 23) % 145 : VIEW_WIDTH - 25 - (index * 29) % 145)
        : 150 + (index * 173) % (VIEW_WIDTH - 300);
      const baseY = 135 + (index * 79) % 500;
      mote.position.set(baseX, baseY);
      this.ambientLayer.addChild(mote);
      this.ambientMotes.push({
        display: mote,
        baseX,
        baseY,
        phase: index * 1.37,
        speed: firefly ? 0.0011 : 0.00032 + (index % 5) * 0.000025,
        drift: firefly ? 16 + (index % 3) * 5 : 9 + (index % 4) * 3,
        rise: firefly ? 10 : 24 + (index % 3) * 8,
        kind: firefly ? "firefly" : "pollen",
      });
    }

    this.forestEyes
      .circle(30, 317, 2).fill({ color: 0xf1c75c })
      .circle(37, 317, 2).fill({ color: 0xf1c75c })
      .circle(VIEW_WIDTH - 35, 532, 2).fill({ color: 0xf1c75c })
      .circle(VIEW_WIDTH - 28, 532, 2).fill({ color: 0xf1c75c });
    this.forestEyes.alpha = 0;
    this.birdFlock
      .moveTo(-12, 0).quadraticCurveTo(-6, -6, 0, 0)
      .quadraticCurveTo(6, -6, 12, 0)
      .moveTo(18, 8).quadraticCurveTo(23, 3, 28, 8)
      .quadraticCurveTo(33, 3, 38, 8)
      .stroke({ color: COLORS.ink, width: 2, alpha: 0.36 });
    this.birdFlock.visible = false;
    this.ambientLayer.addChild(this.forestEyes, this.birdFlock);
  }

  private tickAmbient(): void {
    this.lightRays.alpha = 0.66 + Math.sin(this.elapsedMs * 0.00024) * 0.2;
    this.lightRays.x = Math.sin(this.elapsedMs * 0.00011) * 10;
    this.ambientSways.forEach((sway) => {
      sway.display.rotation = sway.baseRotation + Math.sin(this.elapsedMs * sway.speed + sway.phase) * sway.amplitude;
    });
    this.ambientMotes.forEach((mote) => {
      const time = this.elapsedMs * mote.speed + mote.phase;
      mote.display.position.set(
        mote.baseX + Math.sin(time * 0.81) * mote.drift,
        mote.baseY - ((this.elapsedMs * (mote.kind === "firefly" ? 0.003 : 0.007) + mote.phase * 17) % mote.rise)
          + Math.cos(time) * (mote.kind === "firefly" ? 8 : 2),
      );
      mote.display.alpha = mote.kind === "firefly"
        ? 0.3 + (Math.sin(time * 3.1) + 1) * 0.32
        : 0.34 + Math.sin(time * 1.7) * 0.12;
    });
    const eyeCycle = this.elapsedMs % 11_000;
    const eyeVisible = eyeCycle > 7_100 && eyeCycle < 8_650;
    this.forestEyes.alpha = eyeVisible
      ? Math.min(0.8, Math.sin((eyeCycle - 7_100) / 1_550 * Math.PI) * 0.8)
      : 0;
    const birdCycle = this.elapsedMs % 18_000;
    this.birdFlock.visible = birdCycle < 3_600;
    if (this.birdFlock.visible) {
      const progress = birdCycle / 3_600;
      this.birdFlock.position.set(-70 + progress * (VIEW_WIDTH + 140), 118 + Math.sin(progress * Math.PI * 3) * 9);
      this.birdFlock.alpha = Math.sin(progress * Math.PI) * 0.48;
      this.birdFlock.scale.set(0.78 + Math.sin(progress * Math.PI) * 0.12);
    }
  }

  private drawBoard(): void {
    const board = new Graphics();
    const laneLabels = new Container();
    for (let lane = 0; lane < GAME_CONFIG.lanes; lane += 1) {
      const y = BOARD_TOP + lane * (LANE_HEIGHT + LANE_GAP);
      board.roundRect(BOARD_LEFT, y + 2, BOARD_WORLD_WIDTH, LANE_HEIGHT - 4, 28)
        .fill({ color: lane % 2 === 0 ? COLORS.laneA : COLORS.laneB, alpha: 0.045 });
      const laneNumber = new Text({
        text: String(lane + 1),
        style: { ...TEXT_STYLE, fontSize: 13, fill: COLORS.parchmentDark, fontWeight: "700" },
      });
      laneNumber.anchor.set(0.5);
      laneNumber.position.set(VIEW_WIDTH / 2, y + 13);
      laneLabels.addChild(laneNumber);
    }
    this.container.addChild(board, laneLabels);

    for (const owner of [this.seat, oppositeSeat(this.seat)] as const) {
      for (let lane = 0; lane < GAME_CONFIG.lanes; lane += 1) {
        for (let column = 0; column < GAME_CONFIG.columnsPerSide; column += 1) {
          const cell = new Graphics();
          const x = this.worldToScreenX(buildingWorldX(owner, column));
          const y = laneCenter(lane);
          const ownCell = owner === this.seat;
          drawPlacementMarker(cell, lane, column);
          cell.position.set(x, y);
          if (ownCell) {
            cell.eventMode = "static";
            cell.cursor = "pointer";
            cell.on("pointertap", () => this.handleCellPress(lane, column));
            cell.on("pointerover", function () { cell.alpha = 1; cell.scale.set(1.04); });
            cell.on("pointerout", function () { cell.alpha = 0.72; cell.scale.set(1); });
            cell.alpha = 0.72;
          } else {
            cell.alpha = 0.5;
          }
          this.container.addChild(cell);
        }
      }
    }
  }

  private drawToolbar(): void {
    const panel = createPanel(944, 124, 0.94);
    panel.position.set(228, 666);
    this.container.addChild(panel);
    BUILDING_TYPES.forEach((type, index) => {
      const definition = BUILDING_DEFINITIONS[type];
      const view = createButton({
        width: 108,
        height: 100,
        label: "",
        onPress: () => {
          this.selectedDenId = null;
          this.selectedBuildingType = this.selectedBuildingType === type ? null : type;
          this.updateToolbar(this.state);
          if (this.state) {
            this.updateDenPopup(this.state);
          }
        },
      });
      const content = new Container();
      const { sprite: art, scale: artScale } = createArtSprite(BUILDING_ART_PATHS[type], 58, 48);
      art.scale.set(buildingArtScaleX(type, artScale, true), artScale);
      art.position.set(54, 29);
      const name = new Text({
        text: definition.name,
        style: {
          ...TEXT_STYLE,
          fontSize: definition.name.length > 10 ? 9 : 10,
          fill: COLORS.parchment,
          fontWeight: "700",
        },
      });
      name.anchor.set(0.5);
      name.position.set(54, 58);
      const cost = createCostBadge(definition.cost, 62);
      cost.container.position.set(23, 70);
      content.addChild(art, name, cost.container);
      const cooldownIndicator = new Graphics();
      const cooldownText = new Text({
        text: "",
        style: { ...TEXT_STYLE, fontSize: 11, fill: COLORS.parchment, fontWeight: "700" },
      });
      cooldownText.anchor.set(0.5);
      cooldownText.position.set(91, 17);
      cooldownText.visible = false;
      view.container.addChild(content, cooldownIndicator, cooldownText);
      view.container.position.set(240 + index * 115, 678);
      view.container.on("pointerdown", (event) => this.beginBuildingDrag(type, event));
      view.container.on("globalpointermove", (event) => this.moveBuildingDrag(event));
      view.container.on("pointerup", (event) => this.endBuildingDrag(event));
      view.container.on("pointerupoutside", (event) => this.endBuildingDrag(event));
      this.container.addChild(view.container);
      this.buildButtons.push({
        type,
        view,
        content,
        cooldownIndicator,
        cooldownText,
        cooldownRemainingMs: 0,
        cooldownDurationMs: definition.placementCooldownMs,
        cooldownRatio: 0,
        shakeMs: 0,
        phase: index * 0.9,
      });
    });
  }

  private beginBuildingDrag(type: BuildingType, event: FederatedPointerEvent): void {
    if (!this.state
      || this.state.phase !== "playing"
      || !this.state.players.p1.connected
      || !this.state.players.p2.connected
      || this.state.players[this.seat].gold < BUILDING_DEFINITIONS[type].cost
      || this.state.players[this.seat].buildingCooldowns[type] > 0) {
      return;
    }
    this.buildingDrag?.preview.destroy();
    const point = event.getLocalPosition(this.container);
    const { sprite: preview, scale: previewScale } = createArtSprite(BUILDING_ART_PATHS[type], 70, 66);
    preview.scale.set(buildingArtScaleX(type, previewScale, true), previewScale);
    preview.position.copyFrom(point);
    preview.alpha = 0.78;
    preview.visible = false;
    this.dragLayer.addChild(preview);
    this.buildingDrag = {
      type,
      pointerId: event.pointerId,
      startX: point.x,
      startY: point.y,
      moved: false,
      preview,
    };
    this.selectedDenId = null;
    this.updateDenPopup(this.state);
  }

  private moveBuildingDrag(event: FederatedPointerEvent): void {
    const drag = this.buildingDrag;
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    const point = event.getLocalPosition(this.container);
    if (!drag.moved && Math.hypot(point.x - drag.startX, point.y - drag.startY) >= 8) {
      drag.moved = true;
      drag.preview.visible = true;
    }
    if (drag.moved) {
      const tile = this.findOwnTileAt(point.x, point.y);
      const canPlace = Boolean(tile && (drag.type === "bomb" || !this.tileIsOccupied(tile.lane, tile.column)));
      drag.preview.position.set(
        tile ? this.worldToScreenX(buildingWorldX(this.seat, tile.column)) : point.x,
        tile ? laneCenter(tile.lane) : point.y,
      );
      drag.preview.alpha = canPlace ? 0.92 : 0.4;
      drag.preview.tint = canPlace ? 0xffffff : 0xff8f82;
      drag.preview.rotation = canPlace ? 0 : Math.sin(this.elapsedMs * 0.055) * 0.065;
    }
  }

  private endBuildingDrag(event: FederatedPointerEvent): void {
    const drag = this.buildingDrag;
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    const point = event.getLocalPosition(this.container);
    if (drag.moved) {
      const tile = this.findOwnTileAt(point.x, point.y);
      if (tile && (drag.type === "bomb" || !this.tileIsOccupied(tile.lane, tile.column))) {
        this.actions.placeBuilding(tile.lane, tile.column, drag.type);
      } else {
        this.shakeBuildButton(drag.type);
      }
      this.selectedBuildingType = null;
    }
    drag.preview.destroy();
    this.buildingDrag = null;
    this.updateToolbar(this.state);
  }

  private beginDenQueueDrag(index: number, event: FederatedPointerEvent): void {
    const den = this.selectedDenId
      ? this.state?.buildings.find((building) => building.id === this.selectedDenId)
      : null;
    if (!den || den.type !== "wolf_den" || den.isReleasingQueue || !den.queuedUnits[index]) {
      return;
    }
    const point = event.getLocalPosition(this.denPopup);
    this.selectedDenQueueIndex = index;
    this.denQueueDrag = {
      pointerId: event.pointerId,
      fromIndex: index,
      targetIndex: index,
      startX: point.x,
      startY: point.y,
      moved: false,
    };
    this.updateDenPopup(this.state!);
  }

  private moveDenQueueDrag(event: FederatedPointerEvent): void {
    const drag = this.denQueueDrag;
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    const den = this.selectedDenId
      ? this.state?.buildings.find((building) => building.id === this.selectedDenId)
      : null;
    if (!den || den.queuedUnits.length === 0) {
      this.denQueueDrag = null;
      return;
    }
    const point = event.getLocalPosition(this.denPopup);
    if (!drag.moved && Math.hypot(point.x - drag.startX, point.y - drag.startY) >= 6) {
      drag.moved = true;
    }
    if (!drag.moved) {
      return;
    }
    const firstCenter = DEN_QUEUE_SLOT_START_X + DEN_QUEUE_SLOT_WIDTH / 2;
    drag.targetIndex = Math.max(
      0,
      Math.min(den.queuedUnits.length - 1, Math.round((point.x - firstCenter) / DEN_QUEUE_SLOT_STEP)),
    );
    this.selectedDenQueueIndex = drag.targetIndex;
    this.updateDenPopup(this.state!);
  }

  private endDenQueueDrag(event: FederatedPointerEvent): void {
    const drag = this.denQueueDrag;
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    this.denQueueDrag = null;
    if (drag.moved && drag.targetIndex !== drag.fromIndex && this.selectedDenId) {
      this.actions.reorderUnitQueue(this.selectedDenId, drag.fromIndex, drag.targetIndex);
      this.selectedDenQueueIndex = drag.targetIndex;
    } else {
      this.selectedDenQueueIndex = drag.fromIndex;
    }
    if (this.state) {
      this.updateDenPopup(this.state);
    }
  }

  private findOwnTileAt(x: number, y: number): { lane: number; column: number } | null {
    let best: { lane: number; column: number; distance: number } | null = null;
    for (let lane = 0; lane < GAME_CONFIG.lanes; lane += 1) {
      for (let column = 0; column < GAME_CONFIG.columnsPerSide; column += 1) {
        const tileX = this.worldToScreenX(buildingWorldX(this.seat, column));
        const tileY = laneCenter(lane);
        const dx = Math.abs(x - tileX);
        const dy = Math.abs(y - tileY);
        if (dx <= 32 && dy <= 39) {
          const distance = dx + dy;
          if (!best || distance < best.distance) {
            best = { lane, column, distance };
          }
        }
      }
    }
    return best ? { lane: best.lane, column: best.column } : null;
  }

  private tileIsOccupied(lane: number, column: number): boolean {
    return Boolean(this.state?.buildings.some((building) => (
      building.owner === this.seat && building.lane === lane && building.column === column
    )));
  }

  private handleCellPress(lane: number, column: number): void {
    if (!this.state || this.state.phase !== "playing") {
      return;
    }
    if (!this.selectedBuildingType) {
      this.selectedDenId = null;
      this.updateDenPopup(this.state);
      return;
    }
    if (this.selectedBuildingType !== "bomb" && this.tileIsOccupied(lane, column)) {
      this.shakeBuildButton(this.selectedBuildingType);
      this.showToast("Den byggplatsen är redan upptagen.");
      return;
    }
    const type = this.selectedBuildingType;
    this.selectedBuildingType = null;
    this.actions.placeBuilding(lane, column, type);
    this.updateToolbar(this.state);
  }

  private shakeBuildButton(type: BuildingType): void {
    const button = this.buildButtons.find((entry) => entry.type === type);
    if (button) {
      button.shakeMs = 300;
    }
  }

  private handleBuildingPress(buildingId: string): void {
    const building = this.state?.buildings.find((entry) => entry.id === buildingId);
    if (!building || building.owner !== this.seat) {
      return;
    }
    if (this.selectedBuildingType === "bomb") {
      this.selectedBuildingType = null;
      this.selectedDenId = null;
      this.actions.placeBuilding(building.lane, building.column, "bomb");
      this.updateToolbar(this.state);
      if (this.state) {
        this.updateDenPopup(this.state);
      }
      return;
    }
    if (building.type === "gold_mine") {
      if (building.pendingGold > 0) {
        this.actions.claimGold(building.id);
      }
      return;
    }
    if (building.type === "wolf_den") {
      this.selectedBuildingType = null;
      this.selectedDenQueueIndex = null;
      this.denQueueDrag = null;
      this.selectedDenId = this.selectedDenId === building.id ? null : building.id;
      this.updateToolbar(this.state);
      if (this.state) {
        this.updateDenPopup(this.state);
      }
      return;
    }
    this.selectedDenId = null;
    this.selectedDenQueueIndex = null;
    this.denQueueDrag = null;
    if (this.state) {
      this.updateDenPopup(this.state);
    }
  }

  private handleScenePress(event: FederatedPointerEvent): void {
    if (!this.selectedDenId) {
      return;
    }
    const path = event.composedPath();
    if (path.includes(this.denPopup)) {
      return;
    }
    const selectedVisual = this.buildingVisuals.get(this.selectedDenId);
    if (selectedVisual && path.includes(selectedVisual.container)) {
      return;
    }
    this.selectedDenId = null;
    this.selectedDenQueueIndex = null;
    this.denQueueDrag = null;
    if (this.state) {
      this.updateDenPopup(this.state);
    }
  }

  private worldToScreenX(worldX: number): number {
    return BOARD_LEFT + projectWorldXForViewer(worldX, this.seat);
  }

  private updateLaneGuards(state: GameState): void {
    const activeKeys = new Set<string>();
    for (const owner of [this.seat, oppositeSeat(this.seat)] as const) {
      const isOwn = owner === this.seat;
      const x = this.worldToScreenX(laneEndXForSeat(owner));
      for (let lane = 0; lane < GAME_CONFIG.lanes; lane += 1) {
        const key = owner + ":" + lane;
        activeKeys.add(key);
        const ready = Boolean(state.laneGuards[owner][lane]);
        let visual = this.guardVisuals.get(key);
        if (!visual) {
          const container = new Container();
          const body = new Graphics();
          const { sprite: ram, scale: ramScale } = createArtSprite(LANE_GUARD_ART_PATH, 78, 48);
          container.addChild(ram, body);
          this.guardLayer.addChild(container);
          visual = {
            container,
            ram,
            body,
            owner,
            lane,
            ready,
            phase: lane * 0.81 + (isOwn ? 0 : 1.7),
            ramScale,
          };
          this.guardVisuals.set(key, visual);
        }
        visual.container.position.set(x, laneCenter(lane) + 7);
        visual.body.clear();
        visual.ram.scale.set(isOwn ? visual.ramScale : -visual.ramScale, visual.ramScale);
        if (ready) {
          visual.ram.alpha = 0.98;
          visual.ram.tint = 0xffffff;
        } else {
          if (visual.ready) {
            this.particles.burst(x, laneCenter(lane) + 8, {
              kind: "stone",
              count: 8,
              speedMin: 28,
              speedMax: 88,
              upwardBias: 9,
            });
          }
          visual.ram.alpha = 0.18;
          visual.ram.tint = 0x79675e;
          visual.body.circle(0, 0, 20).stroke({ color: COLORS.danger, width: 3, alpha: 0.7 })
            .moveTo(-13, -13).lineTo(13, 13).stroke({ color: COLORS.danger, width: 4, alpha: 0.8 })
            .moveTo(13, -13).lineTo(-13, 13).stroke({ color: COLORS.danger, width: 4, alpha: 0.8 });
        }
        visual.ready = ready;
      }
    }
    this.guardVisuals.forEach((visual, key) => {
      if (!activeKeys.has(key)) {
        visual.container.destroy({ children: true });
        this.guardVisuals.delete(key);
      }
    });
  }

  private createBuildingVisual(building: BuildingState): BuildingVisual {
    const container = new Container();
    const motion = new Container();
    const body = new Graphics();
    const { sprite: art, scale: artScale } = createArtSprite(BUILDING_ART_PATHS[building.type], 60, 60);
    const accent = new Graphics();
    const activity = new Graphics();
    const queue = new Graphics();
    const health = new Graphics();
    health.position.y = -40;
    const badge = new Container();
    const badgeBackground = new Graphics();
    const badgeProgress = new Graphics();
    const badgeText = new Text({
      text: "",
      style: { ...TEXT_STYLE, fontSize: 13, fontWeight: "700", fill: COLORS.ink },
    });
    badgeText.anchor.set(0.5);
    badge.addChild(badgeProgress, badgeBackground, badgeText);
    badge.position.set(21, -24);
    body.visible = !art.visible;
    motion.addChild(body, art, activity);
    container.addChild(accent, motion, health, queue, badge);
    const visual: BuildingVisual = {
      container,
      motion,
      body,
      art,
      artScale,
      accent,
      activity,
      queue,
      health,
      badge,
      badgeBackground,
      badgeProgress,
      badgeText,
      type: building.type,
      owner: building.owner,
      phase: (Number.parseInt(building.id.replace(/\D/g, ""), 10) || 0) * 0.73,
      spawnMs: 0,
      hitMs: 0,
      recoilMs: 0,
      lastHealth: building.health,
      hovered: false,
      aimDirection: building.owner === this.seat ? 0 : Math.PI,
      hasTarget: false,
    };
    if (building.owner === this.seat) {
      container.eventMode = "static";
      container.cursor = "pointer";
      container.on("pointertap", () => this.handleBuildingPress(building.id));
      container.on("pointerover", function () { visual.hovered = true; });
      container.on("pointerout", function () { visual.hovered = false; });
    }
    this.buildingLayer.addChild(container);
    return visual;
  }

  private updateBuildings(state: GameState): void {
    const activeIds = new Set<string>();
    state.buildings.forEach((building) => {
      activeIds.add(building.id);
      let visual = this.buildingVisuals.get(building.id);
      if (!visual) {
        visual = this.createBuildingVisual(building);
        this.buildingVisuals.set(building.id, visual);
      }
      const isOwn = building.owner === this.seat;
      visual.container.position.set(this.worldToScreenX(building.x), laneCenter(building.lane));
      if (building.health < visual.lastHealth) {
        visual.hitMs = 300;
        const particleKind = building.type === "wall"
          ? "stone"
          : building.type === "wolf_den" || building.type === "sumpgap"
            ? "leaf"
            : "dust";
        this.particles.burst(visual.container.x, visual.container.y + 4, {
          kind: particleKind,
          count: building.type === "wall" ? 7 : 5,
          speedMin: 22,
          speedMax: 72,
          upwardBias: 12,
        });
      }
      visual.lastHealth = building.health;
      if (visual.body.visible) {
        drawBuildingBody(visual.body, building, isOwn);
      }
      const artScaleX = buildingArtScaleX(building.type, visual.artScale, isOwn);
      visual.art.scale.set(artScaleX, visual.artScale);
      visual.activity.scale.x = building.type === "watchtower" ? 1 : Math.sign(artScaleX);
      visual.accent.clear().ellipse(0, 23, 25, 6)
        .fill({ color: isOwn ? COLORS.ownDark : COLORS.enemyDark, alpha: 0.46 });
      if (building.id === this.selectedDenId) {
        visual.accent.circle(0, 0, 35).stroke({ color: COLORS.parchment, width: 3, alpha: 0.95 });
      }
      visual.queue.clear();
      if (building.type === "wolf_den"
        && isOwn
        && (building.queueMode || building.queuedUnits.length > 0 || building.isReleasingQueue)) {
        for (let index = 0; index < GAME_CONFIG.maxDenQueue; index += 1) {
          const unitType = building.queuedUnits[index];
          visual.queue.circle(-24 + index * 12, 34, 4.5)
            .fill({
              color: unitType ? UNIT_QUEUE_COLORS[unitType] : COLORS.ink,
              alpha: unitType ? 0.96 : 0.5,
            })
            .stroke({
              color: unitType ? COLORS.parchment : COLORS.parchmentDark,
              width: 1,
              alpha: unitType ? 0.9 : 0.45,
            });
        }
        if (building.isReleasingQueue) {
          visual.queue.poly([31, 28, 39, 34, 31, 40])
            .fill({ color: COLORS.gold, alpha: 0.9 });
        }
      }
      if (building.type === "watchtower" || building.type === "acorn_catapult") {
        const targets = state.units.filter((unit) => unit.owner !== building.owner && unit.lane === building.lane);
        targets.sort((left, right) => Math.abs(left.x - building.x) - Math.abs(right.x - building.x));
        const target = targets[0];
        const range = building.type === "watchtower" ? GAME_CONFIG.towerRange : GAME_CONFIG.catapultRange;
        visual.hasTarget = Boolean(target && Math.abs(target.x - building.x) <= range);
        if (target) {
          const delta = this.worldToScreenX(target.x) - visual.container.x;
          visual.aimDirection = delta >= 0 ? 0 : Math.PI;
        }
      } else {
        visual.hasTarget = false;
      }
      drawHealthBar(visual.health, building.health, building.maxHealth, 48);
      const pending = building.type === "gold_mine" ? building.pendingGold : 0;
      const cooldown = building.type === "wolf_den"
        || building.type === "sumpgap"
        || building.type === "acorn_catapult"
        ? Math.ceil(building.cooldownRemainingMs / 1_000)
        : 0;
      visual.badgeProgress.clear();
      if (building.type === "gold_mine") {
        const full = pending >= GAME_CONFIG.maxPendingGold;
        const hasReadyGold = pending > 0;
        const progress = full ? 1 : Math.max(0, Math.min(1, building.productionProgressMs / GAME_CONFIG.goldIntervalMs));
        visual.badge.visible = true;
        visual.badgeBackground.clear().circle(0, 0, 12)
          .fill({ color: full ? 0x5cc66e : hasReadyGold ? COLORS.gold : 0x39443f })
          .stroke({ color: COLORS.ink, width: 2 });
        visual.badgeProgress.circle(0, 0, 16)
          .stroke({ color: COLORS.ink, width: 4, alpha: 0.7 });
        if (progress > 0) {
          visual.badgeProgress.moveTo(0, -16)
            .arc(0, 0, 16, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * progress)
            .stroke({
              color: full ? 0x7fe88d : hasReadyGold ? COLORS.gold : COLORS.parchmentDark,
              width: 4,
              alpha: hasReadyGold || full ? 1 : 0.8,
            });
        }
        visual.badgeText.style.fill = hasReadyGold ? COLORS.ink : COLORS.parchmentDark;
        visual.badgeText.text = String(pending);
      } else if (cooldown > 0) {
        visual.badge.visible = true;
        visual.badgeBackground.clear().circle(0, 0, 12)
          .fill({ color: COLORS.parchmentDark })
          .stroke({ color: COLORS.ink, width: 2 });
        visual.badgeText.text = String(cooldown);
      } else {
        visual.badge.visible = false;
      }
      visual.container.alpha = state.players[building.owner].connected ? 1 : 0.72;
    });
    this.buildingVisuals.forEach((visual, id) => {
      if (!activeIds.has(id)) {
        if (!this.suppressRemovalEffects) {
          this.spawnBuildingCollapse(visual);
        }
        visual.container.destroy({ children: true });
        this.buildingVisuals.delete(id);
        if (this.selectedDenId === id) {
          this.selectedDenId = null;
        }
      }
    });
  }

  private createUnitVisual(unit: UnitState): UnitVisual {
    const container = new Container();
    const motion = new Container();
    const body = new Graphics();
    const textures = UNIT_ANIMATION_PATHS[unit.type].map(function (path) { return Texture.from(path); });
    const art = new AnimatedSprite(textures);
    art.anchor.set(0.5, 0.9375);
    const artAvailable = textures.every(function (texture) {
      return texture !== Texture.EMPTY && texture.width > 1 && texture.height > 1;
    });
    const artScale = artAvailable ? Math.min(80 / art.texture.width, 58 / art.texture.height) : 1;
    art.visible = artAvailable;
    art.animationSpeed = unit.type === "rabbit"
      ? 0.16
      : unit.type === "hedgehog"
        ? 0.18
        : unit.type === "beaver"
          ? 0.14
          : 0.13;
    art.loop = true;
    art.gotoAndPlay(Number.parseInt(unit.id.replace(/\D/g, ""), 10) % textures.length || 0);
    body.visible = !artAvailable;
    const health = new Graphics();
    health.position.y = -49;
    const shadow = new Graphics().ellipse(0, 2, 27, 6).fill({ color: COLORS.ink, alpha: 0.36 });
    motion.addChild(body, art);
    container.addChild(shadow, motion, health);
    const x = this.worldToScreenX(unit.x);
    const y = laneCenter(unit.lane) + 27;
    container.position.set(x, y);
    this.unitLayer.addChild(container);
    return {
      container,
      motion,
      body,
      art,
      artScale,
      shadow,
      health,
      type: unit.type,
      owner: unit.owner,
      phase: (Number.parseInt(unit.id.replace(/\D/g, ""), 10) || 0) * 0.91,
      spawnMs: 0,
      hitMs: 0,
      attackMs: -1,
      movingMs: 220,
      lastHealth: unit.health,
      lastServerX: unit.x,
      hopMs: -1,
      hopFromX: x,
      hopToX: x,
      targetX: x,
      targetY: y,
    };
  }

  private updateUnits(state: GameState): void {
    const activeIds = new Set<string>();
    const formationOffsets = calculateUnitFormationOffsets(state, this.seat);
    state.units.forEach((unit) => {
      activeIds.add(unit.id);
      let visual = this.unitVisuals.get(unit.id);
      if (!visual) {
        visual = this.createUnitVisual(unit);
        this.unitVisuals.set(unit.id, visual);
      }
      const isOwn = unit.owner === this.seat;
      if (Math.abs(unit.x - visual.lastServerX) > 0.01) {
        visual.movingMs = 240;
        visual.lastServerX = unit.x;
      }
      if (unit.health < visual.lastHealth) {
        visual.hitMs = 260;
        this.particles.burst(visual.container.x, visual.container.y - 10, {
          kind: "leaf",
          count: 5,
          color: isOwn ? COLORS.own : COLORS.enemy,
          speedMin: 24,
          speedMax: 78,
          upwardBias: 10,
        });
      }
      visual.lastHealth = unit.health;
      if (visual.body.visible) {
        drawUnit(visual.body, unit.type, isOwn);
        visual.body.scale.x = isOwn ? 1 : -1;
      }
      visual.art.scale.set(isOwn ? visual.artScale : -visual.artScale, visual.artScale);
      drawHealthBar(visual.health, unit.health, unit.maxHealth, 50);
      const formationOffset = formationOffsets.get(unit.id) ?? { x: 0, y: 0 };
      visual.targetX = this.worldToScreenX(unit.x) + formationOffset.x;
      visual.targetY = laneCenter(unit.lane) + 27 + formationOffset.y;
    });
    this.unitVisuals.forEach((visual, id) => {
      if (!activeIds.has(id)) {
        if (!this.suppressRemovalEffects) {
          this.spawnUnitPoof(visual);
        }
        visual.container.destroy({ children: true });
        this.unitVisuals.delete(id);
      }
    });
  }

  private spawnBuildingCollapse(visual: BuildingVisual): void {
    const x = visual.container.x;
    const y = visual.container.y;
    if (visual.type === "mushroom") {
      this.particles.burst(x, y + 5, {
        kind: "smoke",
        count: 9,
        color: 0x91b85b,
        speedMin: 18,
        speedMax: 58,
        upwardBias: 34,
      });
      this.particles.burst(x, y, {
        kind: "leaf",
        count: 9,
        color: 0xd94a3a,
        speedMin: 34,
        speedMax: 104,
        upwardBias: 12,
      });
    } else {
      this.particles.burst(x, y + 10, {
        kind: visual.type === "wall" ? "stone" : "dust",
        count: visual.type === "wall" ? 12 : 9,
        speedMin: 24,
        speedMax: 95,
        upwardBias: 14,
      });
      this.particles.burst(x, y + 5, { kind: "leaf", count: 7, speedMin: 24, speedMax: 82, upwardBias: 8 });
    }
    if (!visual.art.visible || visual.art.texture === Texture.EMPTY) {
      return;
    }
    const container = new Container();
    const ghost = new Sprite(visual.art.texture);
    ghost.anchor.set(0.5);
    const artScaleX = visual.art.scale.x;
    const artScaleY = visual.art.scale.y;
    const isMushroom = visual.type === "mushroom";
    ghost.scale.set(artScaleX, artScaleY);
    container.position.set(x, y);
    container.addChild(ghost);
    const fallDirection = visual.owner === this.seat ? -1 : 1;
    this.timedEffects.add(container, 660, (progress) => {
      if (isMushroom) {
        ghost.position.set(fallDirection * Math.sin(progress * Math.PI) * 7, easeOutCubic(progress) * 18);
        ghost.rotation = fallDirection * Math.sin(progress * Math.PI) * 0.12;
        ghost.scale.set(artScaleX * (1 + progress * 0.3), artScaleY * (1 - progress * 0.8));
      } else {
        ghost.position.set(fallDirection * progress * 13, easeOutCubic(progress) * 25);
        ghost.rotation = fallDirection * easeOutCubic(progress) * 0.24;
        ghost.scale.set(artScaleX * (1 - progress * 0.12), artScaleY * (1 - progress * 0.3));
      }
      ghost.alpha = Math.pow(1 - progress, 1.35) * 0.82;
    });
  }

  private spawnUnitPoof(visual: UnitVisual): void {
    const x = visual.container.x;
    const y = visual.container.y - 11;
    this.particles.burst(x, y, {
      kind: "smoke",
      count: 6,
      color: visual.owner === this.seat ? 0x9db393 : 0xb78b82,
      speedMin: 14,
      speedMax: 42,
      upwardBias: 25,
    });
    this.particles.burst(x, y, {
      kind: "leaf",
      count: 8,
      color: visual.owner === this.seat ? COLORS.own : COLORS.enemy,
      speedMin: 28,
      speedMax: 88,
      upwardBias: 10,
    });
    if (!visual.art.visible || visual.art.texture === Texture.EMPTY) {
      return;
    }
    const container = new Container();
    const ghost = new Sprite(visual.art.texture);
    ghost.anchor.set(0.5, 0.9375);
    const ghostScaleX = visual.art.scale.x;
    const ghostScaleY = visual.art.scale.y;
    ghost.scale.set(ghostScaleX, ghostScaleY);
    container.position.set(visual.container.x, visual.container.y);
    container.addChild(ghost);
    const direction = visual.owner === this.seat ? -1 : 1;
    this.timedEffects.add(container, 480, (progress) => {
      ghost.position.set(direction * progress * 10, -Math.sin(progress * Math.PI) * 9 + progress * 13);
      ghost.rotation = direction * progress * 0.16;
      ghost.alpha = Math.pow(1 - progress, 1.8) * 0.78;
      ghost.scale.set(ghostScaleX, ghostScaleY * (1 - progress * 0.35));
    });
  }

  private triggerUnitAttack(event: MatchEvent): void {
    const x = this.worldToScreenX(event.x);
    const y = laneCenter(event.lane) + 27;
    let closest: UnitVisual | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;
    this.unitVisuals.forEach((visual) => {
      if (visual.owner !== event.owner) {
        return;
      }
      const distance = Math.abs(visual.container.x - x) + Math.abs(visual.container.y - y) * 2;
      if (distance < closestDistance) {
        closest = visual;
        closestDistance = distance;
      }
    });
    const attacker = closest as UnitVisual | null;
    if (attacker && closestDistance < 95) {
      attacker.attackMs = 0;
      attacker.movingMs = 0;
    }
  }

  private triggerRabbitHop(event: MatchEvent): void {
    if (event.targetX === undefined) {
      return;
    }
    const startX = this.worldToScreenX(event.x);
    const targetX = this.worldToScreenX(event.targetX);
    const y = laneCenter(event.lane) + 27;
    let closest: UnitVisual | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;
    this.unitVisuals.forEach((visual) => {
      if (visual.owner !== event.owner || visual.type !== "rabbit") {
        return;
      }
      const distance = Math.min(Math.abs(visual.container.x - startX), Math.abs(visual.container.x - targetX))
        + Math.abs(visual.container.y - y) * 2;
      if (distance < closestDistance) {
        closest = visual;
        closestDistance = distance;
      }
    });
    const rabbit = closest as UnitVisual | null;
    if (rabbit && closestDistance < 130) {
      rabbit.hopFromX = startX;
      rabbit.hopToX = targetX;
      rabbit.hopMs = 0;
      rabbit.movingMs = 580;
      rabbit.container.x = startX;
    }
  }

  private triggerBuildingRecoil(
    event: MatchEvent,
    buildingType: "watchtower" | "acorn_catapult" | "sumpgap",
    durationMs: number,
  ): void {
    const x = this.worldToScreenX(event.x);
    const y = laneCenter(event.lane);
    let closest: BuildingVisual | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;
    this.buildingVisuals.forEach((visual) => {
      if (visual.owner !== event.owner || visual.type !== buildingType) {
        return;
      }
      const distance = Math.abs(visual.container.x - x) + Math.abs(visual.container.y - y) * 2;
      if (distance < closestDistance) {
        closest = visual;
        closestDistance = distance;
      }
    });
    const building = closest as BuildingVisual | null;
    if (building && closestDistance < 70) {
      building.recoilMs = durationMs;
      this.particles.burst(x, y + (buildingType === "sumpgap" ? 5 : -24), {
        kind: "smoke",
        count: buildingType === "sumpgap" ? 5 : 3,
        color: buildingType === "sumpgap" ? 0x9fc568 : 0xb9b3a2,
        speedMin: 10,
        speedMax: 28,
        upwardBias: 12,
        durationMinMs: 260,
        durationMaxMs: 480,
      });
    }
  }

  private updateEffects(state: GameState): void {
    state.events.forEach((event) => {
      const age = state.nowMs - event.createdAtMs;
      if (!this.renderedEventIds.has(event.id)) {
        this.renderedEventIds.add(event.id);
        this.createMatchEffect(event, age);
        if (event.kind === "claim" && event.owner === this.seat && event.amount) {
          this.showToast("+" + event.amount + " guld");
        } else if (event.kind === "lane-sweep") {
          this.showToast(event.owner === this.seat
            ? "Rullstocken räddade lane " + (event.lane + 1) + "!"
            : "Motståndarens rullstock rensade lane " + (event.lane + 1) + ".");
        } else if (event.kind === "bomb") {
          this.showToast(event.owner === this.seat ? "Krutbomben detonerade!" : "Motståndaren sprängde fem tiles!");
        } else if (event.kind === "mushroom") {
          this.showToast(event.owner === this.seat
            ? "Flugsvampen tog med sig en fiende!"
            : "Ett av dina djur åt en Flugsvamp!");
        } else if (event.kind === "devour") {
          this.showToast(event.owner === this.seat
            ? "Sumpgapet slukade en fiende!"
            : "Ett av dina djur blev slukat!");
        }
      }
    });
    if (this.renderedEventIds.size > 320) {
      const oldestKeptId = state.nextEventId - 180;
      this.renderedEventIds.forEach((id) => {
        if (id < oldestKeptId) {
          this.renderedEventIds.delete(id);
        }
      });
    }
  }

  private createMatchEffect(event: MatchEvent, initialAgeMs: number): void {
    const x = this.worldToScreenX(event.x);
    const y = laneCenter(event.lane);
    if (event.kind === "build") {
      this.particles.burst(x, y + 23, { kind: "dust", count: 11, speedMin: 18, speedMax: 62, upwardBias: 7 });
      this.particles.burst(x, y + 18, { kind: "leaf", count: 6, speedMin: 22, speedMax: 70, upwardBias: 12 });
      this.addPulseRing(x, y + 10, COLORS.own, 560, initialAgeMs);
    } else if (event.kind === "claim") {
      this.createCoinFlight(event, initialAgeMs);
    } else if (event.kind === "spawn") {
      this.particles.burst(x, y + 16, { kind: "smoke", count: 5, speedMin: 12, speedMax: 30, upwardBias: 18 });
      this.particles.burst(x, y + 17, { kind: "leaf", count: 5, speedMin: 20, speedMax: 58, upwardBias: 9 });
      this.addPulseRing(x, y + 8, event.owner === this.seat ? COLORS.own : COLORS.enemy, 460, initialAgeMs);
    } else if (event.kind === "bomb") {
      this.createBombEffect(event, initialAgeMs);
      this.startShake(720, 4.2);
    } else if (event.kind === "mushroom") {
      this.createMushroomEffect(event, initialAgeMs);
      this.startShake(360, 1.8);
    } else if (event.kind === "devour" && event.targetX !== undefined) {
      this.createDevourEffect(event, initialAgeMs);
      this.triggerBuildingRecoil(event, "sumpgap", 900);
      this.startShake(420, 1.4);
    } else if (event.kind === "hop" && event.targetX !== undefined) {
      this.createHopEffect(event, initialAgeMs);
      this.triggerRabbitHop(event);
    } else if (event.kind === "tower-shot" && event.targetX !== undefined) {
      this.createTowerShotEffect(event, initialAgeMs);
      this.triggerBuildingRecoil(event, "watchtower", 280);
    } else if (event.kind === "catapult-shot" && event.targetX !== undefined) {
      this.createCatapultShotEffect(event, initialAgeMs);
      this.triggerBuildingRecoil(event, "acorn_catapult", 520);
    } else if (event.kind === "hit" && event.targetX !== undefined) {
      this.createHitEffect(event, initialAgeMs);
      this.triggerUnitAttack(event);
    } else if (event.kind === "destroy") {
      this.particles.burst(x, y, { kind: "leaf", count: 10, speedMin: 30, speedMax: 98, upwardBias: 14 });
      this.particles.burst(x, y + 10, { kind: "dust", count: 8, speedMin: 18, speedMax: 64, upwardBias: 7 });
      this.addPulseRing(x, y, COLORS.danger, 620, initialAgeMs);
    } else if (event.kind === "lane-sweep" && event.targetX !== undefined) {
      this.createLaneSweepEffect(event, initialAgeMs);
      this.startShake(1_150, 2.8);
    }
  }

  private addPulseRing(x: number, y: number, color: number, durationMs: number, initialAgeMs = 0): void {
    const container = new Container();
    const ring = new Graphics();
    container.position.set(x, y);
    container.addChild(ring);
    this.timedEffects.add(container, durationMs, (progress) => {
      ring.clear().circle(0, 0, lerp(8, 42, easeOutCubic(progress)))
        .stroke({ color, width: lerp(4, 1, progress), alpha: Math.pow(1 - progress, 1.5) * 0.82 });
    }, initialAgeMs);
  }

  private createCoinFlight(event: MatchEvent, initialAgeMs: number): void {
    const container = new Container();
    const startX = this.worldToScreenX(event.x);
    const startY = laneCenter(event.lane) - 4;
    const targetX = event.owner === this.seat ? 58 : VIEW_WIDTH - 127;
    const targetY = 73;
    const coins: Sprite[] = [];
    for (let index = 0; index < 7; index += 1) {
      const { sprite, scale } = createArtSprite(GOLD_COIN_ART_PATH, 20, 20);
      sprite.scale.set(scale * (0.75 + (index % 3) * 0.08));
      sprite.visible = false;
      container.addChild(sprite);
      coins.push(sprite);
    }
    let arrived = false;
    this.timedEffects.add(container, 1_120, (_progress, ageMs) => {
      coins.forEach((coin, index) => {
        const local = clamp01((ageMs - index * 55) / 720);
        coin.visible = local > 0 && local < 1;
        if (!coin.visible) {
          return;
        }
        const eased = easeOutCubic(local);
        const controlX = lerp(startX, targetX, 0.48) + (event.owner === this.seat ? -45 : 45);
        const controlY = Math.min(startY, targetY) - 110 - index * 3;
        const inverse = 1 - eased;
        coin.position.set(
          inverse * inverse * startX + 2 * inverse * eased * controlX + eased * eased * targetX,
          inverse * inverse * startY + 2 * inverse * eased * controlY + eased * eased * targetY,
        );
        coin.rotation = eased * Math.PI * 5 + index;
        coin.alpha = Math.min(1, local * 5) * Math.min(1, (1 - local) * 7);
      });
      if (!arrived && ageMs >= 980) {
        arrived = true;
        if (event.owner === this.seat) {
          this.ownGoldPulseMs = 420;
        } else {
          this.enemyGoldPulseMs = 420;
        }
        this.particles.burst(targetX, targetY, { kind: "spark", count: 6, color: COLORS.gold, speedMin: 15, speedMax: 44 });
      }
    }, initialAgeMs);
  }

  private createBombEffect(event: MatchEvent, initialAgeMs: number): void {
    const container = new Container();
    const centerX = this.worldToScreenX(event.x);
    const centerY = laneCenter(event.lane);
    const { sprite: bomb, scale: bombScale } = createArtSprite(BUILDING_ART_PATHS.bomb, 58, 70);
    const bombDirection = event.owner === this.seat ? 1 : -1;
    bomb.scale.set(bombDirection * bombScale, bombScale);
    const blast = new Graphics();
    container.addChild(blast, bomb);
    const blastPositions: Array<[number, number]> = [[centerX, centerY]];
    const column = event.column ?? 0;
    if (column > 0) {
      blastPositions.push([this.worldToScreenX(buildingWorldX(event.owner, column - 1)), centerY]);
    }
    if (column < GAME_CONFIG.columnsPerSide - 1) {
      blastPositions.push([this.worldToScreenX(buildingWorldX(event.owner, column + 1)), centerY]);
    }
    if (event.lane > 0) {
      blastPositions.push([centerX, centerY - (LANE_HEIGHT + LANE_GAP)]);
    }
    if (event.lane < GAME_CONFIG.lanes - 1) {
      blastPositions.push([centerX, centerY + (LANE_HEIGHT + LANE_GAP)]);
    }
    let burstTriggered = initialAgeMs > 300;
    this.timedEffects.add(container, 1_180, (_progress, ageMs) => {
      const fuseProgress = clamp01(ageMs / 300);
      bomb.visible = fuseProgress < 1;
      if (bomb.visible) {
        bomb.position.set(
          centerX + bombDirection * Math.sin(ageMs * 0.11) * fuseProgress * 4,
          centerY,
        );
        bomb.scale.set(
          bombDirection * bombScale * (0.86 + fuseProgress * 0.24),
          bombScale * (0.86 + fuseProgress * 0.24),
        );
        bomb.rotation = bombDirection * Math.sin(ageMs * 0.075) * fuseProgress * 0.13;
        blast.clear()
          .circle(centerX + bombDirection * 12, centerY - 31, 2 + Math.sin(ageMs * 0.08) * 1.2)
          .fill({ color: COLORS.gold, alpha: 0.9 });
      } else {
        const explosion = clamp01((ageMs - 300) / 760);
        const alpha = Math.pow(1 - explosion, 1.5);
        blast.clear();
        blastPositions.forEach(([positionX, positionY], index) => {
          const delayed = clamp01(explosion * 1.25 - index * 0.055);
          const radius = 12 + easeOutCubic(delayed) * 49;
          blast.circle(positionX, positionY, radius)
            .fill({ color: COLORS.gold, alpha: alpha * 0.32 })
            .stroke({ color: index === 0 ? COLORS.danger : 0xe9994e, width: 6 - delayed * 3, alpha });
        });
        if (!burstTriggered) {
          burstTriggered = true;
          blastPositions.forEach(([positionX, positionY]) => {
            this.particles.burst(positionX, positionY, {
              kind: "spark",
              count: 7,
              color: COLORS.gold,
              speedMin: 45,
              speedMax: 130,
              upwardBias: 12,
            });
            this.particles.burst(positionX, positionY + 10, {
              kind: "smoke",
              count: 4,
              speedMin: 18,
              speedMax: 48,
              upwardBias: 35,
            });
          });
        }
      }
    }, initialAgeMs);
  }

  private createMushroomEffect(event: MatchEvent, initialAgeMs: number): void {
    const x = this.worldToScreenX(event.x);
    const targetX = this.worldToScreenX(event.targetX ?? event.x);
    const y = laneCenter(event.lane) - 4;
    const container = new Container();
    const cloud = new Graphics();
    container.position.set((x + targetX) / 2, y);
    container.addChild(cloud);
    this.timedEffects.add(container, 1_050, (progress, ageMs) => {
      const eased = easeOutCubic(progress);
      const alpha = Math.pow(1 - progress, 1.55);
      cloud.clear();
      for (let puff = 0; puff < 9; puff += 1) {
        const angle = puff * Math.PI * 2 / 9 + ageMs * 0.0028;
        const radius = 8 + eased * (24 + (puff % 3) * 5);
        const puffX = Math.cos(angle) * radius + Math.sin(ageMs * 0.006 + puff) * 5;
        const puffY = Math.sin(angle) * radius * 0.58 - eased * 18;
        cloud.circle(puffX, puffY, 7 + eased * (7 + puff % 4))
          .fill({ color: puff % 3 === 0 ? 0xd8e99c : 0x7fa34c, alpha: alpha * 0.24 });
      }
      cloud.circle(0, -eased * 12, 10 + eased * 31)
        .stroke({ color: 0xb8d96e, width: 4 - progress * 2, alpha: alpha * 0.72 });
    }, initialAgeMs);
    this.particles.burst(x, y + 13, {
      kind: "smoke",
      count: 12,
      color: 0x9fc568,
      speedMin: 24,
      speedMax: 74,
      upwardBias: 38,
      durationMinMs: 620,
      durationMaxMs: 1_050,
    });
    this.particles.burst(x, y + 9, {
      kind: "leaf",
      count: 10,
      color: 0xd94a3a,
      speedMin: 38,
      speedMax: 110,
      upwardBias: 15,
    });
    this.particles.burst(targetX, y, {
      kind: "spark",
      count: 8,
      color: 0xe9efbd,
      speedMin: 24,
      speedMax: 72,
      upwardBias: 10,
    });
  }

  private createDevourEffect(event: MatchEvent, initialAgeMs: number): void {
    if (event.targetX === undefined) {
      return;
    }
    const mouthX = this.worldToScreenX(event.x);
    const targetX = this.worldToScreenX(event.targetX);
    const y = laneCenter(event.lane) + 5;
    const container = new Container();
    const tongue = new Graphics();
    const gulp = new Graphics();
    container.addChild(tongue, gulp);
    let impactTriggered = initialAgeMs > GAME_CONFIG.sumpgapImpactDelayMs;
    this.timedEffects.add(container, GAME_CONFIG.sumpgapImpactDelayMs + 550, (_progress, ageMs) => {
      const reach = ageMs < GAME_CONFIG.sumpgapImpactDelayMs
        ? easeOutCubic(clamp01(ageMs / GAME_CONFIG.sumpgapImpactDelayMs))
        : 1 - easeOutCubic(clamp01(
          (ageMs - GAME_CONFIG.sumpgapImpactDelayMs) / 330,
        ));
      const endX = lerp(mouthX, targetX, reach);
      const bendY = y - 12 - Math.sin(reach * Math.PI) * 15;
      tongue.clear()
        .moveTo(mouthX, y)
        .quadraticCurveTo((mouthX + endX) / 2, bendY, endX, y)
        .stroke({ color: 0x71382f, width: 8, alpha: Math.min(1, reach * 3) * 0.9 })
        .moveTo(mouthX, y - 1)
        .quadraticCurveTo((mouthX + endX) / 2, bendY - 2, endX, y - 1)
        .stroke({ color: 0xcf8060, width: 2.5, alpha: Math.min(1, reach * 3) * 0.78 })
        .circle(endX, y, 7 + Math.sin(ageMs * 0.035) * 1.5)
        .fill({ color: 0x8b493d, alpha: reach > 0.04 ? 0.9 : 0 });
      const chew = clamp01((ageMs - 650) / 330);
      gulp.clear();
      if (chew > 0) {
        gulp.circle(mouthX, y, 15 + easeOutCubic(chew) * 24)
          .stroke({
            color: 0xb7d57a,
            width: 5 - chew * 3,
            alpha: Math.pow(1 - chew, 1.4) * 0.72,
          });
      }
      if (!impactTriggered && ageMs >= GAME_CONFIG.sumpgapImpactDelayMs) {
        impactTriggered = true;
        this.particles.burst(targetX, y, {
          kind: "leaf",
          count: 8,
          color: 0x86a957,
          speedMin: 22,
          speedMax: 68,
          upwardBias: 8,
        });
        this.particles.burst(mouthX, y + 5, {
          kind: "smoke",
          count: 7,
          color: 0x9fc568,
          speedMin: 12,
          speedMax: 38,
          upwardBias: 24,
        });
      }
    }, initialAgeMs);
  }

  private createCatapultShotEffect(event: MatchEvent, initialAgeMs: number): void {
    if (event.targetX === undefined) {
      return;
    }
    const startX = this.worldToScreenX(event.x);
    const endX = this.worldToScreenX(event.targetX);
    const startY = laneCenter(event.lane) - 28;
    const endY = laneCenter(event.lane) + 3;
    const container = new Container();
    const trail = new Graphics();
    const impact = new Graphics();
    const acorn = new Graphics()
      .ellipse(0, 2, 8, 11).fill({ color: 0xa86b2e }).stroke({ color: COLORS.ink, width: 1.5 })
      .ellipse(0, -5, 9, 4).fill({ color: 0x624528 })
      .moveTo(0, -9).lineTo(3, -14).stroke({ color: 0x624528, width: 2 });
    container.addChild(trail, impact, acorn);
    let impacted = initialAgeMs > GAME_CONFIG.catapultImpactDelayMs;
    this.timedEffects.add(container, GAME_CONFIG.catapultImpactDelayMs + 360, (_progress, ageMs) => {
      const flight = clamp01(ageMs / GAME_CONFIG.catapultImpactDelayMs);
      const eased = easeOutCubic(flight);
      const x = lerp(startX, endX, eased);
      const y = lerp(startY, endY, eased) - Math.sin(eased * Math.PI) * 92;
      const previous = Math.max(0, eased - 0.08);
      const trailX = lerp(startX, endX, previous);
      const trailY = lerp(startY, endY, previous) - Math.sin(previous * Math.PI) * 92;
      trail.clear().moveTo(trailX, trailY).lineTo(x, y)
        .stroke({ color: 0xd6b266, width: 4, alpha: (1 - flight) * 0.55 });
      acorn.visible = flight < 1;
      acorn.position.set(x, y);
      acorn.rotation = ageMs * 0.018 * Math.sign(endX - startX || 1);
      impact.clear();
      if (flight >= 1) {
        const splash = clamp01((ageMs - GAME_CONFIG.catapultImpactDelayMs) / 360);
        impact.circle(endX, endY, 12 + easeOutCubic(splash) * GAME_CONFIG.catapultSplashRadius)
          .fill({ color: 0x9a7138, alpha: Math.pow(1 - splash, 1.5) * 0.16 })
          .stroke({
            color: COLORS.gold,
            width: 5 - splash * 3,
            alpha: Math.pow(1 - splash, 1.2) * 0.66,
          });
      }
      if (!impacted && flight >= 1) {
        impacted = true;
        this.particles.burst(endX, endY, {
          kind: "leaf",
          count: 14,
          color: 0x8ea34f,
          speedMin: 36,
          speedMax: 110,
          upwardBias: 16,
        });
        this.particles.burst(endX, endY + 8, {
          kind: "dust",
          count: 10,
          color: 0x9a7138,
          speedMin: 22,
          speedMax: 72,
          upwardBias: 9,
        });
      }
    }, initialAgeMs);
  }

  private createHopEffect(event: MatchEvent, initialAgeMs: number): void {
    if (event.targetX === undefined) {
      return;
    }
    const startX = this.worldToScreenX(event.x);
    const endX = this.worldToScreenX(event.targetX);
    const y = laneCenter(event.lane) + 17;
    const container = new Container();
    const trail = new Graphics();
    container.addChild(trail);
    this.timedEffects.add(container, 620, (progress) => {
      const shown = easeOutCubic(progress);
      const currentX = lerp(startX, endX, shown);
      const currentY = y - Math.sin(shown * Math.PI) * 55;
      trail.clear()
        .moveTo(startX, y)
        .quadraticCurveTo((startX + endX) / 2, y - 55, currentX, currentY)
        .stroke({ color: COLORS.parchment, width: 3, alpha: (1 - progress) * 0.55 });
    }, initialAgeMs);
  }

  private createTowerShotEffect(event: MatchEvent, initialAgeMs: number): void {
    if (event.targetX === undefined) {
      return;
    }
    const startX = this.worldToScreenX(event.x);
    const endX = this.worldToScreenX(event.targetX);
    const startY = laneCenter(event.lane) - 26;
    const endY = laneCenter(event.lane) + 5;
    const container = new Container();
    const trail = new Graphics();
    const projectile = new Graphics()
      .moveTo(-9, 0).lineTo(8, 0).stroke({ color: 0xe8d39a, width: 2.5 })
      .poly([-7, -4, -11, 0, -7, 4]).fill({ color: COLORS.gold });
    container.addChild(trail, projectile);
    let impacted = initialAgeMs > GAME_CONFIG.towerImpactDelayMs;
    this.timedEffects.add(container, GAME_CONFIG.towerImpactDelayMs + 190, (_progress, ageMs) => {
      const flight = clamp01(ageMs / GAME_CONFIG.towerImpactDelayMs);
      const eased = easeOutCubic(flight);
      const x = lerp(startX, endX, eased);
      const y = lerp(startY, endY, eased) - Math.sin(eased * Math.PI) * 16;
      const previous = Math.max(0, eased - 0.09);
      const trailX = lerp(startX, endX, previous);
      const trailY = lerp(startY, endY, previous) - Math.sin(previous * Math.PI) * 16;
      trail.clear().moveTo(trailX, trailY).lineTo(x, y)
        .stroke({ color: COLORS.gold, width: 3, alpha: (1 - flight) * 0.72 });
      projectile.visible = flight < 1;
      projectile.position.set(x, y);
      projectile.rotation = Math.atan2(y - trailY, x - trailX);
      if (!impacted && flight >= 1) {
        impacted = true;
        this.particles.burst(endX, endY, { kind: "spark", count: 8, color: COLORS.gold, speedMin: 28, speedMax: 85 });
        this.addPulseRing(endX, endY, COLORS.gold, 360);
      }
    }, initialAgeMs);
  }

  private createHitEffect(event: MatchEvent, initialAgeMs: number): void {
    if (event.targetX === undefined) {
      return;
    }
    const x = this.worldToScreenX(event.targetX);
    const y = laneCenter(event.lane) + 4;
    const container = new Container();
    const slash = new Graphics();
    container.position.set(x, y);
    container.addChild(slash);
    this.timedEffects.add(container, 390, (progress) => {
      const alpha = Math.pow(1 - progress, 1.4);
      const size = 13 + easeOutCubic(progress) * 14;
      slash.clear()
        .moveTo(-size, -size * 0.55).lineTo(size, size * 0.4)
        .moveTo(-size * 0.55, size * 0.75).lineTo(size * 0.65, -size * 0.7)
        .stroke({ color: COLORS.parchment, width: 4 - progress * 2, alpha });
    }, initialAgeMs);
    this.particles.burst(x, y, { kind: "leaf", count: 4, color: event.owner === this.seat ? COLORS.enemy : COLORS.own, speedMin: 22, speedMax: 70 });
  }

  private createLaneSweepEffect(event: MatchEvent, initialAgeMs: number): void {
    if (event.targetX === undefined) {
      return;
    }
    const startX = this.worldToScreenX(event.x);
    const endX = this.worldToScreenX(event.targetX);
    const y = laneCenter(event.lane) + 7;
    const container = new Container();
    const trail = new Graphics();
    const grass = new Graphics();
    const { sprite: ram, scale: ramScale } = createArtSprite(LANE_GUARD_ART_PATH, 96, 60);
    ram.scale.set(endX >= startX ? ramScale : -ramScale, ramScale);
    container.addChild(grass, trail, ram);
    let dustStep = Math.floor(initialAgeMs / 110);
    this.timedEffects.add(container, 1_420, (progress, ageMs) => {
      const travel = easeOutCubic(Math.min(1, progress * 1.12));
      const x = lerp(startX, endX, travel);
      const direction = Math.sign(endX - startX) || 1;
      grass.clear();
      const wakeStart = lerp(startX, endX, Math.max(0, travel - 0.2));
      for (let blade = 0; blade < 10; blade += 1) {
        const bladeX = lerp(wakeStart, x, blade / 9);
        grass.moveTo(bladeX, y + 19).quadraticCurveTo(bladeX - direction * 8, y + 11, bladeX - direction * 13, y + 15)
          .stroke({ color: 0x596a35, width: 2, alpha: (1 - progress) * 0.5 });
      }
      trail.clear().moveTo(wakeStart, y + 14).lineTo(x, y + 14)
        .stroke({ color: 0xb77a3c, width: 9, alpha: (1 - progress) * 0.35 });
      ram.position.set(x, y + Math.sin(ageMs * 0.045) * 2.5);
      ram.rotation = Math.sin(ageMs * 0.035) * 0.035;
      ram.alpha = Math.min(1, (1 - progress) * 4);
      const nextDustStep = Math.floor(ageMs / 110);
      if (nextDustStep > dustStep && progress < 0.8) {
        dustStep = nextDustStep;
        this.particles.burst(x - direction * 25, y + 17, {
          kind: "dust",
          count: 3,
          speedMin: 12,
          speedMax: 35,
          upwardBias: 4,
          durationMinMs: 300,
          durationMaxMs: 600,
        });
        this.particles.burst(x - direction * 18, y + 13, {
          kind: "leaf",
          count: 2,
          speedMin: 18,
          speedMax: 45,
          upwardBias: 5,
        });
      }
    }, initialAgeMs);
  }

  private updateToolbar(state: GameState | null): void {
    const ownGold = state?.players[this.seat].gold || 0;
    if (state && this.selectedBuildingType
      && state.players[this.seat].buildingCooldowns[this.selectedBuildingType] > 0) {
      this.selectedBuildingType = null;
    }
    this.buildButtons.forEach((entry) => {
      const definition = BUILDING_DEFINITIONS[entry.type];
      const cooldownRemainingMs = state?.players[this.seat].buildingCooldowns[entry.type] ?? 0;
      const enabled = Boolean(state
        && state.phase === "playing"
        && state.players.p1.connected
        && state.players.p2.connected
        && ownGold >= definition.cost
        && cooldownRemainingMs <= 0);
      entry.view.setSelected(this.selectedBuildingType === entry.type);
      entry.view.setEnabled(enabled);
      entry.content.alpha = enabled ? 1 : cooldownRemainingMs > 0 ? 0.5 : 0.34;
      const wasCoolingDown = entry.cooldownRemainingMs > 0;
      entry.cooldownRemainingMs = cooldownRemainingMs;
      entry.cooldownDurationMs = definition.placementCooldownMs;
      const serverRatio = clamp01(cooldownRemainingMs / definition.placementCooldownMs);
      if (!wasCoolingDown && cooldownRemainingMs > 0) {
        entry.cooldownRatio = serverRatio;
      } else if (Math.abs(entry.cooldownRatio - serverRatio) > 0.12) {
        entry.cooldownRatio = serverRatio;
      }
      this.drawBuildButtonCooldown(entry);
    });
    if (state) {
      const ownVote = state.restartVotes[this.seat];
      const enemyVote = state.restartVotes[oppositeSeat(this.seat)];
      this.restartButton.label.text = ownVote
        ? "Väntar på svar…"
        : enemyVote ? "Godkänn omstart" : "Starta om";
      this.restartButton.setSelected(enemyVote && !ownVote);
      this.restartButton.setEnabled(state.phase !== "waiting" && !ownVote);
    } else {
      this.restartButton.setEnabled(false);
    }
  }

  private updateDenPopup(state: GameState): void {
    const den = this.selectedDenId
      ? state.buildings.find((building) => (
        building.id === this.selectedDenId && building.owner === this.seat && building.type === "wolf_den"
      ))
      : null;
    if (!den
      || state.phase !== "playing"
      || !state.players.p1.connected
      || !state.players.p2.connected) {
      this.selectedDenId = null;
      this.selectedDenQueueIndex = null;
      this.denQueueDrag = null;
      this.denPopup.visible = false;
      return;
    }

    if (this.selectedDenQueueIndex !== null
      && this.selectedDenQueueIndex >= den.queuedUnits.length) {
      this.selectedDenQueueIndex = null;
    }
    const denX = this.worldToScreenX(den.x);
    const desiredX = denX < VIEW_WIDTH / 2 ? denX + 42 : denX - 448;
    this.denPopup.position.set(
      Math.max(BOARD_LEFT + 4, Math.min(VIEW_WIDTH - BOARD_LEFT - 410, desiredX)),
      Math.max(BOARD_TOP, Math.min(430, laneCenter(den.lane) - 85)),
    );
    this.denPopup.visible = true;

    const ownGold = state.players[this.seat].gold;
    const cooldown = Math.ceil(den.cooldownRemainingMs / 1_000);
    const queueFull = den.queuedUnits.length >= GAME_CONFIG.maxDenQueue;
    DEN_UNIT_TYPES.forEach((unitType) => {
      const action = this.denActions.get(unitType);
      if (!action) {
        return;
      }
      const enabled = !den.isReleasingQueue
        && den.cooldownRemainingMs <= 0
        && ownGold >= UNIT_DEFINITIONS[unitType].cost
        && (!den.queueMode || !queueFull);
      action.view.setEnabled(enabled);
      action.view.setSelected(den.queueMode && den.queuedUnits.includes(unitType));
      action.content.alpha = enabled ? 1 : den.queuedUnits.includes(unitType) ? 0.7 : 0.34;
      action.marker.text = cooldown > 0 ? String(cooldown) : queueFull && den.queueMode ? "FULL" : "";
    });

    this.denModeButton.label.text = den.queueMode ? "LÄGE: KÖ" : "LÄGE: DIREKT";
    this.denModeButton.setSelected(den.queueMode);
    this.denModeButton.setEnabled(!den.isReleasingQueue);
    const selectedType = this.selectedDenQueueIndex === null
      ? null
      : den.queuedUnits[this.selectedDenQueueIndex] ?? null;
    this.denRemoveButton.label.text = selectedType
      ? "TA BORT +" + UNIT_DEFINITIONS[selectedType].cost
      : "VÄLJ DJUR";
    this.denRemoveButton.setSelected(selectedType !== null);
    this.denRemoveButton.setEnabled(selectedType !== null && !den.isReleasingQueue);
    const releaseCooldown = Math.ceil(den.queueReleaseRemainingMs / 1_000);
    this.denReleaseNextButton.label.text = den.isReleasingQueue
      ? "SLÄPPER…"
      : releaseCooldown > 0 ? "NÄSTA OM " + releaseCooldown + "s" : "SLÄPP NÄSTA";
    this.denReleaseNextButton.setSelected(false);
    this.denReleaseNextButton.setEnabled(
      den.queuedUnits.length > 0 && !den.isReleasingQueue && den.queueReleaseRemainingMs <= 0,
    );
    this.denReleaseAllButton.label.text = den.isReleasingQueue
      ? "SLÄPPER " + den.queuedUnits.length + "…"
      : "SLÄPP ALLA";
    this.denReleaseAllButton.setSelected(den.isReleasingQueue);
    this.denReleaseAllButton.setEnabled(den.queuedUnits.length > 0 && !den.isReleasingQueue);
    this.denQueueText.text = "KÖ " + den.queuedUnits.length + "/" + GAME_CONFIG.maxDenQueue
      + (den.queuedUnits.length > 1 ? " • DRA FÖR ATT ORDNA" : "");
    this.denQueueIcons.forEach((icon, index) => {
      const unitType = den.queuedUnits[index];
      const slot = this.denQueueSlots[index]!;
      const background = this.denQueueSlotBackgrounds[index]!;
      const selected = index === this.selectedDenQueueIndex;
      const dragSource = index === this.denQueueDrag?.fromIndex;
      background.clear().roundRect(0, 0, DEN_QUEUE_SLOT_WIDTH, DEN_QUEUE_SLOT_HEIGHT, 8)
        .fill({
          color: unitType ? UNIT_QUEUE_COLORS[unitType] : COLORS.ink,
          alpha: unitType ? selected ? 0.72 : 0.42 : 0.35,
        })
        .stroke({
          color: selected ? COLORS.gold : COLORS.parchmentDark,
          width: selected ? 2.5 : 1.3,
          alpha: unitType ? 0.9 : 0.35,
        });
      slot.eventMode = unitType && !den.isReleasingQueue ? "static" : "none";
      slot.cursor = unitType && !den.isReleasingQueue ? dragSource ? "grabbing" : "grab" : "default";
      if (!unitType) {
        icon.visible = false;
        return;
      }
      const texture = Texture.from(UNIT_ART_PATHS[unitType]);
      icon.texture = texture;
      const scale = Math.min(19 / texture.width, 19 / texture.height);
      icon.scale.set(scale);
      icon.visible = true;
      icon.tint = dragSource && this.denQueueDrag?.moved ? 0xffe49c : 0xffffff;
      icon.alpha = dragSource && this.denQueueDrag?.moved ? 0.62 : 1;
    });
  }

  private updateOverlay(state: GameState): void {
    let mode = "";
    if (state.phase === "waiting") {
      mode = "waiting";
    } else if (state.phase === "finished") {
      mode = "finished:" + state.winner + ":" + state.restartVotes.p1 + ":" + state.restartVotes.p2;
    } else if (!state.players.p1.connected || !state.players.p2.connected) {
      mode = "paused";
    }
    if (mode === this.overlayMode) {
      return;
    }
    this.overlayMode = mode;
    this.overlayLayer.removeChildren().forEach(function (child) { child.destroy({ children: true }); });
    if (!mode) {
      return;
    }

    const shade = new Graphics().rect(0, 0, VIEW_WIDTH, 635).fill({ color: COLORS.ink, alpha: 0.48 });
    const panel = createPanel(650, mode === "waiting" ? 300 : mode.startsWith("finished") ? 280 : 230, 0.97);
    panel.position.set(375, mode === "waiting" ? 205 : 240);
    this.overlayLayer.addChild(shade, panel);

    const heading = new Text({
      text: mode === "waiting"
        ? "VÄNTAR PÅ MOTSTÅNDARE"
        : mode === "paused"
          ? "MATCHEN ÄR PAUSAD"
          : state.winner === "draw"
            ? "OAVGJORT"
            : state.winner === this.seat ? "SEGER!" : "SKOGEN FÖLL",
      style: { ...TEXT_STYLE, fontSize: 34, fill: mode.startsWith("finished") ? COLORS.gold : COLORS.parchment, fontWeight: "700" },
    });
    heading.anchor.set(0.5);
    heading.position.set(VIEW_WIDTH / 2, mode === "waiting" ? 264 : 300);
    this.overlayLayer.addChild(heading);

    const body = new Text({
      text: mode === "waiting"
        ? "Öppna länken på den andra datorn:\n" + window.location.origin + "/game/" + this.roomCode
        : mode === "paused"
          ? "Den andra spelaren har tappat anslutningen.\nSpelet fortsätter automatiskt när båda är tillbaka."
          : state.mode === "ai"
            ? "Matchen är avgjord. Starta en ny omgång när du vill möta Skogsväktaren igen."
            : "Matchen är avgjord. Båda spelarna måste godkänna för att starta en ny omgång.",
      style: { ...TEXT_STYLE, fontSize: 18, fill: COLORS.parchmentDark, align: "center", lineHeight: 28, wordWrap: true, wordWrapWidth: 570 },
    });
    body.anchor.set(0.5);
    body.position.set(VIEW_WIDTH / 2, mode === "waiting" ? 337 : 370);
    this.overlayLayer.addChild(body);

    if (mode === "waiting") {
      const copyButton = createButton({
        width: 260,
        height: 54,
        label: "Kopiera matchlänk",
        fontSize: 18,
        onPress: () => this.copyShareLink(),
      });
      copyButton.container.position.set(570, 410);
      this.overlayLayer.addChild(copyButton.container);
      const code = new Text({
        text: this.roomCode,
        style: { ...TEXT_STYLE, fontSize: 22, fill: COLORS.gold, fontWeight: "700", letterSpacing: 5 },
      });
      code.anchor.set(0.5);
      code.position.set(VIEW_WIDTH / 2, 485);
      this.overlayLayer.addChild(code);
    } else if (mode.startsWith("finished")) {
      const ownVote = state.restartVotes[this.seat];
      const enemyVote = state.restartVotes[oppositeSeat(this.seat)];
      const restart = createButton({
        width: 250,
        height: 52,
        label: ownVote ? "Väntar på motståndaren…" : enemyVote ? "Godkänn omstart" : "Starta om",
        fontSize: 16,
        onPress: () => this.actions.requestRestart(),
      });
      restart.setSelected(enemyVote && !ownVote);
      restart.setEnabled(!ownVote);
      restart.container.position.set(575, 408);
      this.overlayLayer.addChild(restart.container);
    }
  }

  private copyShareLink(): void {
    const link = window.location.origin + "/game/" + this.roomCode;
    const fallback = function (): boolean {
      const field = document.createElement("textarea");
      field.value = link;
      field.style.position = "fixed";
      field.style.opacity = "0";
      document.body.appendChild(field);
      field.select();
      const copied = document.execCommand("copy");
      field.remove();
      return copied;
    };
    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(link)
        .then(() => this.showToast("Matchlänken är kopierad."))
        .catch(() => this.showToast(fallback() ? "Matchlänken är kopierad." : "Kunde inte kopiera länken."));
    } else {
      this.showToast(fallback() ? "Matchlänken är kopierad." : "Kunde inte kopiera länken.");
    }
  }
}
